/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useWindowSize } from '../hooks/useWindowSize';
import { useData } from '../backend/firebase';
import { createPortal } from 'react-dom';

// --- 0. SETTINGS HOOK ---
const usePersistentState = (key, initialValue) => {
    const [state, setState] = useState(() => {
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) { return initialValue; }
    });
    useEffect(() => { window.localStorage.setItem(key, JSON.stringify(state)); }, [key, state]);
    return [state, setState];
};

const DDS_WASM_VERSION = "1.0.2";

// --- 1. CORE BRIDGE LOGIC & CONSTANTS ---
const SUITS = ['S', 'H', 'D', 'C'];
const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
const SEATS = ['W', 'N', 'E', 'S']; 

const SUIT_SYMBOLS = { 'S': '♠', 'H': '♥', 'D': '♦', 'C': '♣' };
const SUIT_COLORS = { 'S': '#111', 'H': '#E53E3E', 'D': '#E53E3E', 'C': '#111' };

const getRankValue = (rank) => {
    if (rank === 'A') return 14; if (rank === 'K') return 13;
    if (rank === 'Q') return 12; if (rank === 'J') return 11;
    if (rank === 'T' || rank === '10') return 10;
    return parseInt(rank);
};

const getVulnerability = (boardNum) => {
    if (!boardNum) return { NS: false, EW: false, label: 'None' };
    const vuls = [
        { NS: false, EW: false, label: 'None' }, { NS: true,  EW: false, label: 'N/S' },
        { NS: false, EW: true,  label: 'E/W' },  { NS: true,  EW: true,  label: 'Both' },
        { NS: true,  EW: false, label: 'N/S' },  { NS: false, EW: true,  label: 'E/W' },
        { NS: true,  EW: true,  label: 'Both' }, { NS: false, EW: false, label: 'None' },
        { NS: false, EW: true,  label: 'E/W' },  { NS: true,  EW: true,  label: 'Both' },
        { NS: false, EW: false, label: 'None' }, { NS: true,  EW: false, label: 'N/S' },
        { NS: true,  EW: true,  label: 'Both' }, { NS: false, EW: false, label: 'None' },
        { NS: true,  EW: false, label: 'N/S' },  { NS: false, EW: true,  label: 'E/W' }
    ];
    return vuls[(boardNum - 1) % 16];
};

const parseContractStr = (str) => {
    if (!str || str.trim() === '' || str.toUpperCase() === 'P' || str.toUpperCase() === 'PASS') {
        return { isPass: true, contract: 'Pass', declarer: '', result: '', trump: 'N' };
    }
    
    // Normalize: remove all spaces, convert NT to N
    let s = str.toUpperCase().replace(/\s+/g, '').replace('NT', 'N');
    
    // 1. Find Level and Suit (must be at the start)
    const lvlSuit = s.match(/^([1-7])([CDHSN])/);
    if (!lvlSuit) return { isPass: true, contract: str, declarer: '', result: '', trump: 'N' };
    
    const level = lvlSuit[1];
    const suit = lvlSuit[2];
    
    // FIX: Chop off the level and suit so we don't accidentally read "S"pades or "N"T as a player!
    const remainder = s.substring(lvlSuit[0].length);
    
    // 2. Find Seat (Declarer) anywhere in the REMAINDER
    const seatMatch = remainder.match(/[NESW]/);
    const declarer = seatMatch ? seatMatch[0] : '';
    
    // 3. Find Double (x or xx) anywhere in the REMAINDER
    const dblMatch = remainder.match(/(XX|X)/);
    const dbl = dblMatch ? dblMatch[1].toLowerCase() : '';
    
    // 4. Find Result (+1, -2, =) at the end
    const resMatch = remainder.match(/([-=+])(\d*)$/);
    const res = resMatch ? resMatch[1] : '';
    const tricks = resMatch ? resMatch[2] : '';

    return {
        isPass: false, level, suit, declarer, dbl, res, tricks, trump: suit
    };
};

const getOptimalContracts = (table) => {
    if (!table) return "--";
    const strains = ['NT', 'S', 'H', 'D', 'C'];
    const formatStr = (side1, side2) => {
        const makes = strains.map(s => {
            const tricks = Math.max(table[`${side1}_${s}`] || 0, table[`${side2}_${s}`] || 0);
            return tricks > 6 ? `${tricks - 6}<span style="color:${(s === 'H' || s === 'D') ? '#FF3366' : (s === 'NT' || s === 'N') ? '#fff' : '#fff'}">${SUIT_SYMBOLS[s] || 'NT'}</span>` : null;
        }).filter(Boolean);
        return makes.length > 0 ? makes.join(' ') : 'Pass';
    };
    
    // Using dangerouslySetInnerHTML allows us to render the red suits correctly
    return (
        <span style={{ display: 'flex', gap: 10 }}>
            <span>NS: <span dangerouslySetInnerHTML={{ __html: formatStr('N', 'S') }} /></span>
            <span style={{color: 'rgba(255,255,255,0.2)'}}>|</span>
            <span>EW: <span dangerouslySetInnerHTML={{ __html: formatStr('E', 'W') }} /></span>
        </span>
    );
};

const getParScore = (table, vulData) => {
    if (!table) return null;
    
    // Standard duplicate bridge scoring calculator
    const getScore = (level, suit, tricks, vul) => {
        const target = level + 6;
        if (tricks < target) {
            const down = target - tricks;
            // Sacrifices in a par algorithm are ALWAYS assumed to be doubled
            if (vul) return -(200 + (down - 1) * 300);
            if (down === 1) return -100;
            if (down === 2) return -300;
            if (down === 3) return -500;
            return -(500 + (down - 3) * 300);
        }
        let trickPts = (suit === 'C' || suit === 'D') ? level * 20 : level * 30;
        if (suit === 'NT') trickPts = 40 + (level - 1) * 30;
        let overtricks = (tricks - target) * ((suit === 'C' || suit === 'D') ? 20 : 30);
        let gameBonus = trickPts >= 100 ? (vul ? 500 : 300) : 50;
        let slamBonus = level === 6 ? (vul ? 750 : 500) : (level === 7 ? (vul ? 1500 : 1000) : 0);
        return trickPts + gameBonus + overtricks + slamBonus;
    };

    // Strains strictly ordered by bidding hierarchy
    const strains = ['C', 'D', 'H', 'S', 'NT'];
    const bids = [];
    for (let l = 1; l <= 7; l++) {
        for (let s of strains) {
            const nsT = Math.max(table[`N_${s}`] ?? 0, table[`S_${s}`] ?? 0);
            const ewT = Math.max(table[`E_${s}`] ?? 0, table[`W_${s}`] ?? 0);
            bids.push({
                level: l, suit: s, nsTricks: nsT, ewTricks: ewT,
                nsScore: getScore(l, s, nsT, vulData.NS),
                ewScore: getScore(l, s, ewT, vulData.EW)
            });
        }
    }

    // 1. Find the highest making scores to start the bidding war
    let nsBestMake = bids.filter(b => b.nsTricks >= b.level + 6).reduce((max, b) => b.nsScore > max.nsScore ? b : max, {nsScore: -9999, idx: -1});
    let ewBestMake = bids.filter(b => b.ewTricks >= b.level + 6).reduce((max, b) => b.ewScore > max.ewScore ? b : max, {ewScore: -9999, idx: -1});
    
    if (nsBestMake.nsScore === -9999 && ewBestMake.ewScore === -9999) return <span style={{color: '#fff', fontWeight: 'bold'}}>Pass</span>;

    let leadingSide = nsBestMake.nsScore > ewBestMake.ewScore ? 'NS' : 'EW';
    let bestMake = leadingSide === 'NS' ? nsBestMake : ewBestMake;
    
    let bestIdx = bids.findIndex(b => b.level === bestMake.level && b.suit === bestMake.suit);
    let parBid = bestMake;
    let parScore = leadingSide === 'NS' ? bestMake.nsScore : bestMake.ewScore;
    let isDoubled = false;

    // 2. Sweep all higher bids to see if the opponent should sacrifice
    for (let i = bestIdx + 1; i < bids.length; i++) {
        const sacBid = bids[i];
        const oppSide = leadingSide === 'NS' ? 'EW' : 'NS';
        const sacScore = oppSide === 'EW' ? sacBid.ewScore : sacBid.nsScore;
        
        // If the penalty for sacrificing is better than defending the current contract
        if (sacScore > -parScore) {
            parBid = sacBid;
            parScore = sacScore;
            leadingSide = oppSide;
            isDoubled = parBid[`${leadingSide === 'NS' ? 'ns' : 'ew'}Tricks`] < parBid.level + 6;
        }
    }

    const color = (parBid.suit === 'H' || parBid.suit === 'D') ? '#FF3366' : '#fff';
    const tricks = leadingSide === 'NS' ? parBid.nsTricks : parBid.ewTricks;
    const diff = tricks - (parBid.level + 6);
    const resStr = diff === 0 ? '=' : (diff > 0 ? `+${diff}` : `${diff}`);

    return (
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', background: 'rgba(0,0,0,0.4)', padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)' }}>
            <span style={{color: '#fff', fontWeight: '900'}}>{leadingSide}</span>
            <span style={{color: '#fff', fontWeight: 'bold'}}>{parBid.level}<span style={{color}}>{SUIT_SYMBOLS[parBid.suit] || 'NT'}</span>{isDoubled ? 'x' : ''} {resStr}</span>
            <span style={{color: 'rgba(255,255,255,0.2)'}}>|</span>
            <span style={{color: parScore > 0 ? '#4ADE80' : '#FF8A8A', fontWeight: 'bold'}}>{parScore > 0 ? `+${parScore}` : parScore}</span>
        </span>
    );
};

const formatContract = (contractStr) => {
    if (!contractStr || contractStr.trim() === '' || contractStr.toUpperCase() === 'P' || contractStr.toUpperCase() === 'PASS') {
        return <span style={{color: '#8F9EB5'}}>Pass</span>;
    }
    
    const match = contractStr.trim().match(/^([1-7])([CDHSN])(.*)$/i);
    if (!match) return <span>{contractStr}</span>;
    
    const level = match[1];
    const suit = match[2].toUpperCase();
    const rest = match[3];
    
    const SYMBOLS = { 'S': '♠', 'H': '♥', 'D': '♦', 'C': '♣', 'N': 'NT' };
    const COLORS = { 'S': '#fff', 'H': '#FF3366', 'D': '#FF3366', 'C': '#fff', 'N': '#fff' };
    const WIN_COLOR = '#6cbd8c'; 
    const LOSE_COLOR = '#ed7666';
    
    let modifiers = rest;
    let resultSpan = null; 

    const resultMatch = rest.match(/^(.*?)([-=+](?:\d+)?)$/);
    if (resultMatch) {
        modifiers = resultMatch[1]; 
        const resultText = resultMatch[2]; 
        const isWin = resultText.startsWith('=') || resultText.startsWith('+');
        resultSpan = <span style={{color: '#fff', marginLeft: 6}}>{resultText}</span>;
    }
    
    return (
        <span style={{display: 'inline-flex', alignItems: 'center'}}>
            <span style={{color:'#fff'}}>
                {level}<span style={{color: COLORS[suit]}}>{SYMBOLS[suit]}</span>{modifiers}
            </span>
            {resultSpan}
        </span>
    );
};


const reconstructHands = (handsStr) => {
    if (!handsStr) return { S: [], W: [], N: [], E: [], dealer: 'S' };
    const dealerMap = { '1': 'S', '2': 'W', '3': 'N', '4': 'E' };
    const dealer = dealerMap[handsStr.charAt(0)] || 'S';
    const parts = handsStr.substring(1).split(',');
    
    let parsedHands = { 0: parts[0] || '', 1: parts[1] || '', 2: parts[2] || '', 3: parts[3] || '' };

    // Safely translates a LIN string chunk into an array of card objects
    const toArr = (s) => {
        let arr = []; let cur = '';
        for (let i = 0; i < s.length; i++) {
            if (SUITS.includes(s[i])) cur = s[i];
            else if (cur) {
                let r = s[i];
                // Safely convert '10' or '1' to 'T' without skipping next characters if natively 'T'
                if (r === '1' && s[i+1] === '0') { r = 'T'; i++; }
                else if (r === '1') r = 'T';
                
                if (RANKS.includes(r)) {
                    arr.push({ suit: cur, rank: r, id: `${cur}${r}` });
                }
            }
        }
        return arr;
    };

    let hands = { 
        S: toArr(parsedHands[0]), 
        W: toArr(parsedHands[1]), 
        N: toArr(parsedHands[2]), 
        E: toArr(parsedHands[3]) 
    };

    // Find the missing hand and populate it accurately
    let missingIdx = -1;
    Object.keys(parsedHands).forEach(k => { if (parsedHands[k] === '') missingIdx = parseInt(k); });
    
    if (missingIdx !== -1) {
        let deck = {}; SUITS.forEach(s => deck[s] = [...RANKS]);
        
        // Remove strictly known cards
        Object.values(hands).forEach(handArr => {
            handArr.forEach(card => {
                deck[card.suit] = deck[card.suit].filter(r => r !== card.rank);
            });
        });

        // Deal remainder to the missing hand
        let missingHandArr = [];
        SUITS.forEach(s => {
            deck[s].forEach(r => missingHandArr.push({ suit: s, rank: r, id: `${s}${r}` }));
        });
        
        const idxMap = {0: 'S', 1: 'W', 2: 'N', 3: 'E'};
        hands[idxMap[missingIdx]] = missingHandArr;
    }

    return { ...hands, dealer };
};

// --- DOUBLE DUMMY HELPERS ---
const getTrumpInt = (suit) => {
    const map = { 'S': 0, 'H': 1, 'D': 2, 'C': 3, 'N': 4 };
    return map[suit] !== undefined ? map[suit] : 4;
};

const getSeatInt = (seat) => {
    const map = { 'N': 0, 'E': 1, 'S': 2, 'W': 3 };
    return map[seat] !== undefined ? map[seat] : 0;
};

const convertToPBN = (hands, currentTrick = []) => {
    // Extract just the IDs of the cards currently on the table
    const trickIds = currentTrick.length === 4 ? [] : currentTrick.map(t => t.card.id);
    // const trickIds = currentTrick.map(t => t.card.id);

    const formatHand = (handArray) => {
        // Keep cards that are completely unplayed, OR are currently in the active trick
        const validCards = handArray.filter(c => !c.isPlayed || trickIds.includes(c.id));
        const getSuit = (suit) => validCards.filter(c => c.suit === suit).map(c => c.rank === '10' ? 'T' : c.rank).join('');
        return `${getSuit('S')}.${getSuit('H')}.${getSuit('D')}.${getSuit('C')}`;
    };

    const maxCards = Math.max(
        hands.N.filter(c => !c.isPlayed || trickIds.includes(c.id)).length,
        hands.E.filter(c => !c.isPlayed || trickIds.includes(c.id)).length,
        hands.S.filter(c => !c.isPlayed || trickIds.includes(c.id)).length,
        hands.W.filter(c => !c.isPlayed || trickIds.includes(c.id)).length
    );

    if (maxCards > 13) {
        console.error(`🚨 ABORTING DDS: Invalid state. A player has ${maxCards} cards.`);
        return null; 
    }

    return `N:${formatHand(hands.N)} ${formatHand(hands.E)} ${formatHand(hands.S)} ${formatHand(hands.W)}`;
};


const PlayingCard = ({ card, hidden, isPlayed, ddScore, onClick, onDoubleClick, alignRight }) => {
    if (!card) return <div style={{width: 32, height: 48, borderRadius: 4, border: '1px dashed rgba(255,255,255,0.1)'}} />;

    let badge = null;
    if (ddScore !== undefined && ddScore !== null) {
        const isSuccess = ddScore >= 0;
        const text = ddScore === 0 ? '=' : (ddScore > 0 ? `+${ddScore}` : `${ddScore}`);

        badge = (
            <div style={{
                position: 'absolute', bottom: 10, left: alignRight ? 'auto' : 0, right: alignRight ? 0 : 'auto',
                background: isSuccess ? '#26A65B' : '#E53E3E', color: '#fff', fontSize: 9, fontWeight: '900',
                padding: '1px 4px', borderRadius: 4, boxShadow: '0 2px 4px rgba(0,0,0,0.8)', pointerEvents: 'none',
                whiteSpace: 'nowrap', zIndex: 9999
            }}>
                {text}
            </div>
        );
    }

    if (hidden) {
        return (
            <div 
                onClick={onClick} onDoubleClick={onDoubleClick}
                style={{
                    position: 'relative', width: 32, height: 48, borderRadius: 4, 
                    background: 'linear-gradient(135deg, #2D2B3F 25%, #1A1C29 100%)', 
                    border: '1px solid rgba(255,255,255,0.2)', cursor: onClick ? 'pointer' : 'default',
                    boxShadow: '-1px 1px 3px rgba(0,0,0,0.4)', zIndex: badge ? 10 : 1
                }}
            >
                {badge} {/* Badge renders OVER the card back! */}
            </div>
        );
    }

    return (
        <div 
            onClick={onClick} onDoubleClick={onDoubleClick}
            style={{ 
                position: 'relative', width: 32, height: 48, background: '#F0F2F5', 
                border: '0.5px solid rgba(78, 78, 78, 0.2)', borderRadius: 4, 
                cursor: onClick ? 'pointer' : 'default', opacity: isPlayed ? 0.3 : 1,
                boxShadow: '-1px 1px 3px rgba(0,0,0,0.4)', zIndex: badge ? 10 : 1 
            }}
        >
            {badge}
            <div style={{ position: 'absolute', top: 2, left: alignRight ? 'auto' : 3, right: alignRight ? 3 : 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                <div style={{ color: SUIT_COLORS[card.suit], fontSize: 13, fontWeight: 'bold' }}>{card.rank}</div>
                <div style={{ color: SUIT_COLORS[card.suit], fontSize: 14, marginTop: -2 }}>{SUIT_SYMBOLS[card.suit]}</div>
            </div>
        </div>
    );
};


const PlayerBadge = ({ seat, name, realName, isVul, teamColor, onClick }) => {
    const bgColor = isVul ? 'rgba(169, 68, 68, 0.95)' : 'rgba(45, 90, 60, 0.95)';
    return (
        <div onClick={onClick} style={{ 
            display: 'flex', alignItems: 'center', gap: 6, background: bgColor, 
            padding: '4px 10px', borderRadius: 8, border: `1px solid ${teamColor || 'rgba(188, 188, 188, 0.74)'}`,
            boxShadow: `0 4px 10px rgba(0,0,0,0.3)`, cursor: onClick ? 'pointer' : 'default'
        }}>
            <div style={{ fontSize: 11, color: '#E0E0E0', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={realName || name}>
                {name || 'Unknown'}
            </div>
        </div>
    );
};

const PlayerHand = ({ seat, pName, realName, isVul, teamColor, cards, ddScores = {}, trump, onCardClick, onBadgeClick, isHiddenRegion, flippedCards = {}, isActiveTurn, leadSuit }) => {
    const grouped = { S: [], H: [], D: [], C: [] };
    cards.forEach(c => grouped[c.suit].push(c));

    const getSuitOrder = (tSuit) => {
        if (!tSuit || tSuit === 'N' || tSuit === 'S') return ['S', 'H', 'C', 'D'];
        if (tSuit === 'H') return ['H', 'S', 'D', 'C'];
        if (tSuit === 'D') return ['D', 'S', 'H', 'C'];
        if (tSuit === 'C') return ['C', 'H', 'S', 'D'];
        return ['S', 'H', 'C', 'D'];
    };

    const orderedSuits = getSuitOrder(trump);
    const badgeNode = <PlayerBadge seat={seat} name={pName} realName={realName} isVul={isVul} teamColor={teamColor} onClick={() => onBadgeClick && onBadgeClick(seat)} />;

    // --- LEGAL PLAY CALCULATION ---
    const unplayed = cards.filter(c => !c.isPlayed);
    const hasLeadSuit = leadSuit && unplayed.some(c => c.suit === leadSuit);
    // Notice: isBiddingPhase makes all cards legal
    const isLegal = (c) => isActiveTurn && (!hasLeadSuit || c.suit === leadSuit);

    // --- NORTH & SOUTH HANDS: Single Horizontal Row ---
    if (seat === 'N' || seat === 'S') {
        const lineCards = orderedSuits
            .flatMap(suit => [...grouped[suit]].sort((a,b) => getRankValue(b.rank) - getRankValue(a.rank)))
            .filter(c => !c.isPlayed);
        
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: 'max-content' }}>
                {seat === 'N' && badgeNode}
                <div style={{ display: 'flex', flexDirection: 'row', background: 'rgba(0,0,0,0.0)', padding: '12px', borderRadius: 8 }}>
                    {lineCards.length === 0 && <div style={{width: 32, height: 48}} />}
                    
                    {lineCards.map((c, i) => {
                        const overlap = 16; 
                        const cardIsLegal = !c.isPlayed && isLegal(c);
                        
                        return (
                            <div key={c.id} style={{ position: 'relative', marginLeft: i > 0 ? -overlap : 0, zIndex: i }}>
                                {/* FLOATING ACCENT FOR N/S: Doesn't push the cards! */}
                                {cardIsLegal && (
                                    <div style={{
                                        position: 'absolute', left: '5%', right: '35%',
                                        [seat === 'N' ? 'top' : 'bottom']: -4, height: 4,
                                        background: '#FFD700', borderRadius: 2,
                                        // boxShadow: '0 0 8px 1px rgba(255, 215, 0, 0.6)',
                                        zIndex: 100
                                    }} />
                                )}
                                <div style={{ opacity: c.isPlayed ? 0.2 : 1, transition: '0.3s' }}>
                                    <PlayingCard card={c} hidden={isHiddenRegion && !flippedCards[c.id]} isPlayed={false} ddScore={!c.isPlayed ? ddScores[c.id] : null} onClick={() => onCardClick && onCardClick(c, seat)} />
                                </div>
                            </div>
                        );
                    })}
                </div>
                {seat === 'S' && badgeNode}
            </div>
        );
    }

    // --- EAST & WEST HANDS: 4 Horizontal Rows ---
    const isWest = seat === 'W';
    const alignLayout = isWest ? 'flex-start' : 'flex-end';
    const activeSuits = orderedSuits.filter(suit => grouped[suit].some(c => !c.isPlayed));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: alignLayout, gap: 10, width: 'max-content' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, padding: '12px', borderRadius: 8, justifyContent: 'center', alignItems: alignLayout }}>
                {activeSuits.length === 0 && <div style={{minHeight: 48}} />}

                {orderedSuits.map((suit, rowIdx) => {
                    // let suitCards = [...grouped[suit]];
                    let suitCards = [...grouped[suit]].filter(c => !c.isPlayed);
                    if (suitCards.length === 0) return <div key={suit} style={{minHeight: 36, minWidth: 32}} />;
                    
                    // SORTING LOGIC: High cards on the "outside"
                    if (isWest) {
                        suitCards.sort((a, b) => getRankValue(b.rank) - getRankValue(a.rank));
                    } else {
                        suitCards.sort((a, b) => getRankValue(a.rank) - getRankValue(b.rank));
                    }
                    
                    const isLegalSuit = isActiveTurn && suitCards.some(c => isLegal(c));
                    const overlap = 18; 
                    
                    return (
                        <div key={suit} style={{ 
                            position: 'relative', // Anchor for floating absolute bar
                            display: 'flex', flexDirection: 'row', minHeight: 48, minWidth: 32, 
                            justifyContent: alignLayout, marginTop: rowIdx > 0 ? -12 : 0,
                            transition: '0.1s'
                        }}>
                            {/* FLOATING ACCENT FOR E/W: Doesn't push the cards! */}
                            {isLegalSuit && (
                                <div style={{
                                    position: 'absolute',
                                    [isWest ? 'left' : 'right']: -4,
                                    top: '0%', bottom: '20%', width: 4,
                                    background: '#FFD700', borderRadius: 2,
                                    // boxShadow: '0 0 8px 1px rgba(255, 215, 0, 0.6)',
                                    zIndex: 100
                                }} />
                            )}
                            {suitCards.map((c, i) => {
                                const hasScore = ddScores[c.id] !== undefined;
                                
                                // 1. Calculate the base horizontal Z-index
                                let baseZ = i;
                                if (isWest) baseZ = i; 
                                else baseZ = 50 - i; 

                                // 2. Add the vertical boost (lower suits get higher z-indexes)
                                const cascadeZ = baseZ + (rowIdx * 20);

                                return (
                                    <div key={c.id} style={{ position: 'relative', marginLeft: i > 0 ? -(32 - overlap) : 0, zIndex: cascadeZ }}>
                                        <div style={{opacity: c.isPlayed ? 0.2 : 1, transition: '0.3s'}}>
                                            {/* Pass alignRight=true ONLY for West cards */}
                                            <PlayingCard card={c} hidden={isHiddenRegion && !flippedCards[c.id]} isPlayed={false} ddScore={!c.isPlayed ? ddScores[c.id] : null} alignRight={!isWest} onClick={() => onCardClick && onCardClick(c, seat)} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
            {badgeNode}
        </div>
    );
};

const HandDiagram = ({ hands, ddScores }) => {
    const formatSuit = (cards) => {
        if (!cards || cards.length === 0) return '-';
        return cards.map((c) => {
            const rankStr = c.rank === 'T' ? '10' : c.rank;
            const score = ddScores && ddScores[c.id] !== undefined ? ddScores[c.id] : null;
            
            // New legible pills for DD Scores
            let scoreSpan = null;
            if (score !== null && !c.isPlayed) {
                const isSuccess = score >= 0;
                const text = score === 0 ? '=' : (score > 0 ? `+${score}` : `${score}`);
                scoreSpan = (
                    <span style={{ 
                        background: isSuccess ? '#1E4620' : '#4A1515', color: isSuccess ? '#4ADE80' : '#FF8A8A', 
                        padding: '1px 4px', borderRadius: 4, fontSize: 9, marginLeft: 3, verticalAlign: 'middle', fontWeight: 'bold' 
                    }}>
                        {text}
                    </span>
                );
            }
            return (
                <span key={c.id} style={{ opacity: c.isPlayed ? 0.3 : 1, marginRight: 6, display: 'inline-flex', alignItems: 'center' }}>
                    {rankStr}{scoreSpan}
                </span>
            );
        });
    };

    const HandBlock = ({ hand, title }) => (
        <div style={{display: 'flex', flexDirection: 'column', fontSize: 13, lineHeight: 1.4, background: 'rgba(0,0,0,0.0)', padding: 12, borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)', minWidth: 140 }}>
            <div style={{color: '#A0AABF', fontSize: 10, fontWeight: 'bold', marginBottom: 4, textTransform: 'uppercase'}}>{title}</div>
            <div style={{color: '#fff'}}><span style={{color: '#A0AABF'}}>{SUIT_SYMBOLS['S']}</span> {formatSuit(hand.filter(c => c.suit === 'S'))}</div>
            <div style={{color: '#fff'}}><span style={{color: '#FF3366'}}>{SUIT_SYMBOLS['H']}</span> {formatSuit(hand.filter(c => c.suit === 'H'))}</div>
            <div style={{color: '#fff'}}><span style={{color: '#FF3366'}}>{SUIT_SYMBOLS['D']}</span> {formatSuit(hand.filter(c => c.suit === 'D'))}</div>
            <div style={{color: '#fff'}}><span style={{color: '#A0AABF'}}>{SUIT_SYMBOLS['C']}</span> {formatSuit(hand.filter(c => c.suit === 'C'))}</div>
        </div>
    );

    return (
        <div className="ha-table-container">
            <div className="ha-hand-north"><HandBlock hand={hands.N} title="North" /></div>
            <div className="ha-hand-west"><HandBlock hand={hands.W} title="West" /></div>
            <div className="ha-hand-east"><HandBlock hand={hands.E} title="East" /></div>
            <div className="ha-hand-south"><HandBlock hand={hands.S} title="South" /></div>
        </div>
    );
};

const getAuctionState = (sequence) => {
    let lastBidValue = -1;
    let lastBidSeat = -1;
    let isDoubled = false;
    let isRedoubled = false;
    let passes = 0;
    let turn = 0; // 0=Dealer, 1, 2, 3

    const suitVals = { 'C':0, 'D':1, 'H':2, 'S':3, 'N':4 };

    for (let call of sequence) {
        const c = call.split('!')[0].toUpperCase().trim();
        if (c === 'P' || c === 'PASS') {
            passes++;
        } else if (c === 'X' || c === 'D') {
            isDoubled = true; passes = 0;
        } else if (c === 'XX' || c === 'R') {
            isRedoubled = true; passes = 0;
        } else {
            const match = c.match(/^([1-7])([CDHSN])/);
            if (match) {
                lastBidValue = parseInt(match[1]) * 5 + suitVals[match[2]];
                lastBidSeat = turn;
                isDoubled = false; isRedoubled = false; passes = 0;
            }
        }
        turn = (turn + 1) % 4;
    }

    const isAuctionOver = (lastBidValue !== -1 && passes >= 3) || (lastBidValue === -1 && passes >= 4);
    const canDouble = lastBidValue !== -1 && !isDoubled && !isRedoubled && (turn % 2 !== lastBidSeat % 2);
    const canRedouble = isDoubled && !isRedoubled && (turn % 2 === lastBidSeat % 2);

    return { lastBidValue, canDouble, canRedouble, isAuctionOver };
};

const getContractFromAuction = (sequence, dealerSeat) => {
    let lastBid = null;
    let lastBidSeat = null;
    let dbl = '';
    let turn = SEATS.indexOf(dealerSeat) !== -1 ? SEATS.indexOf(dealerSeat) : 3;
    let firstBids = { NS: {}, EW: {} };

    for (let call of sequence) {
        const c = call.split('!')[0].toUpperCase().trim();
        const side = (turn === 1 || turn === 3) ? 'NS' : 'EW'; // N=1, S=3, W=0, E=2
        
        if (c === 'X' || c === 'D') dbl = 'x';
        else if (c === 'XX' || c === 'R') dbl = 'xx';
        else if (c !== 'P' && c !== 'PASS') {
            const match = c.match(/^([1-7])([CDHSN])/);
            if (match) {
                lastBid = { level: match[1], suit: match[2] };
                lastBidSeat = SEATS[turn];
                dbl = '';
                if (!firstBids[side][match[2]]) firstBids[side][match[2]] = SEATS[turn];
            }
        }
        turn = (turn + 1) % 4;
    }

    if (!lastBid) return { isPass: true, contract: 'Pass', declarer: '', result: '', trump: 'N', level: 0 };
    
    const winningSide = (lastBidSeat === 'N' || lastBidSeat === 'S') ? 'NS' : 'EW';
    return {
        isPass: false, level: lastBid.level, suit: lastBid.suit,
        declarer: firstBids[winningSide][lastBid.suit],
        dbl, res: '', tricks: '', trump: lastBid.suit
    };
};

const BiddingBox = ({ bidding, alerts, dealer, isExpanded, onClick, vulData }) => {
    const [activeAlert, setActiveAlert] = useState(null);
    if (!bidding) return null; // Allow empty arrays ([]) to render the blank grid!
    
    const startIdx = SEATS.indexOf(dealer) !== -1 ? SEATS.indexOf(dealer) : 3;
    const cells = Array(startIdx).fill({ call: '', alert: '' });
    
    bidding.forEach((b, i) => {
        // Look up the parallel alert by index!
        const alertText = (alerts && alerts[i]) ? alerts[i] : '';
        cells.push({ call: b, alert: alertText });
    });

    const rows = [];
    for (let i = 0; i < cells.length; i += 4) rows.push(cells.slice(i, i + 4));

    const parseAlert = (text) => text.replace(/!([CDHSN])/g, (m, suit) => SUIT_SYMBOLS[suit] || m);

    const formatBid = (call) => {
        if (!call) return null;
        const c = call.toUpperCase();
        if (c === 'P' || c === 'PASS') return <span style={{color: '#fff', fontWeight: 'bold', fontSize: 13}}>Pass</span>;
        if (c === 'X' || c === 'D') return <span style={{color: '#ed7666', fontWeight: 'bold'}}>x</span>;
        if (c === 'XX' || c === 'R') return <span style={{color: '#6C5DD3', fontWeight: 'bold'}}>xx</span>;
        
        const match = c.match(/^([1-7])([CDHSN])/);
        if (match) {
            const SYMBOLS = { 'S': '♠', 'H': '♥', 'D': '♦', 'C': '♣', 'N': 'NT' };
            const COLORS = { 'S': '#fff', 'H': '#FF3366', 'D': '#FF3366', 'C': '#fff', 'N': '#fff' };
            return (
                <span style={{fontWeight: 'bold', fontSize: 13}}>
                    <span style={{color: '#fff'}}>{match[1]}</span>
                    <span style={{color: COLORS[match[2]]}}>{SYMBOLS[match[2]]}</span>
                </span>
            );
        }
        return <span style={{color: '#fff'}}>{c}</span>;
    };

    const HeaderCell = ({ seat, isVul, isLast }) => (
        <th style={{ padding: '4px 0', width: '25%', background: isVul ? 'rgb(169, 68, 68)' : '#F0F2F5', color: isVul ? '#fff' : '#111', fontSize: 11, fontWeight: '900', borderRight: isLast ? 'none' : '1px solid rgba(0,0,0,0.1)', borderBottom: '1px solid rgba(0,0,0,0.2)' }}>{seat}</th>
    );
    return (
        <div onClick={onClick} className="ha-hide-scroll" style={{ background: '#1A1D26', borderRadius: 12, border: '2px solid rgba(255,255,255,0.1)', minWidth: 150, width: 'fit-content', height: 140, cursor: onClick ? 'pointer' : 'default', transform: isExpanded ? 'scale(1)' : 'scale(0.85)', transformOrigin: 'center', transition: 'all 0.2s', overflowY: 'auto', overflowX: 'hidden', msOverflowStyle: 'none', scrollbarWidth: 'none', padding: 0, boxShadow: 'inset 0 0 20px rgba(0,0,0,0.6)' }}>
            <table style={{width: '100%', textAlign: 'center', borderCollapse: 'collapse', tableLayout: 'fixed'}}>
                <thead style={{position: 'sticky', top: 0, zIndex: 2}}>
                    <tr>
                        <HeaderCell seat="W" isVul={vulData?.EW} /> <HeaderCell seat="N" isVul={vulData?.NS} />
                        <HeaderCell seat="E" isVul={vulData?.EW} /> <HeaderCell seat="S" isVul={vulData?.NS} isLast={true} />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rIdx) => (
                        <tr key={rIdx}>
                            {row.map((cell, cIdx) => (
                                <td key={cIdx} style={{padding: '3px 2px', borderBottom: '1px solid rgba(255,255,255,0.02)', borderRight: cIdx === 3 ? 'none' : '1px solid rgba(255,255,255,0.02)', position: 'relative'}}>
                                    {cell.call && (
                                        <div 
                                            onClick={(e) => { if(cell.alert) { e.stopPropagation(); setActiveAlert(activeAlert === `${rIdx}-${cIdx}` ? null : `${rIdx}-${cIdx}`); } }}
                                            style={{ 
                                                display: 'inline-block', 
                                                background: cell.alert ? 'rgba(255, 51, 102, 0.15)' : 'transparent', // Subtle red tint
                                                borderRadius: 4, // Clean rectangle instead of a squished oval
                                                border: cell.alert ? '1px solid #FF3366' : '1px solid transparent',
                                                padding: '2px 4px', 
                                                cursor: cell.alert ? 'pointer' : 'default', 
                                                transition: '0.2s'
                                            }}
                                        >
                                            {formatBid(cell.call)}
                                        </div>
                                    )}
                                    {activeAlert === `${rIdx}-${cIdx}` && (
                                        <div style={{
                                            position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                                            background: '#fff', color: '#000', padding: '6px 10px', borderRadius: 6,
                                            fontSize: 12, fontWeight: 'bold', zIndex: 100, whiteSpace: 'nowrap',
                                            boxShadow: '0 4px 15px rgba(0,0,0,0.6)'
                                        }}>
                                            <span dangerouslySetInnerHTML={{ __html: parseAlert(cell.alert).replace(/([♥♦])/g, '<span style="color:#FF3366">$1</span>').replace(/([♠♣])/g, '<span style="color:#111">$1</span>') }} />
                                            <div style={{position:'absolute', top:'100%', left:'50%', transform:'translateX(-50%)', borderTop:'6px solid #fff', borderLeft:'6px solid transparent', borderRight:'6px solid transparent'}} />
                                        </div>
                                    )}
                                </td>
                            ))}
                            {[...Array(4 - row.length)].map((_, i) => <td key={'pad'+i} style={{borderBottom: '2px solid rgba(255,255,255,0.00)', borderRight: (row.length + i) === 3 ? 'none' : '2px solid rgba(255,255,255,0.00)'}}></td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const TrickTracker = ({ trickHistory }) => {
    const [openIndex, setOpenIndex] = useState(null);

    useEffect(() => {
        const closeAll = () => setOpenIndex(null);
        window.addEventListener('click', closeAll);
        return () => window.removeEventListener('click', closeAll);
    }, []);

    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', padding: '6px 10px', background: 'rgba(136, 136, 136, 0)', borderRadius: '6px', border: '0px solid rgba(255, 255, 255, 0.37)', width: 'max-content' }}>
            {[...Array(13)].map((_, i) => {
                const trick = trickHistory[i];
                if (!trick) return <div key={i} style={{ width: '12px', height: '30px', flexShrink: 0, borderBottom: '2px solid rgba(255,255,255,0.0)' }} />;

                const isNS = trick.winner === 'N' || trick.winner === 'S';
                const isOpen = openIndex === i;

                return (
                    <div 
                        key={i} 
                        onClick={(e) => { e.stopPropagation(); setOpenIndex(isOpen ? null : i); }} 
                        style={{ 
                            position: 'relative', 
                            width: '12px', // Consistent horizontal spacing
                            display: 'flex',
                            alignItems: 'flex-end',
                            zIndex: i, 
                            cursor: 'pointer' 
                        }}
                    >
                        <div style={{
                            width: isNS ? 20 : 30, 
                            height: isNS ? 30 : 20,
                            flexShrink: 0,
                            // Subtle gradient for more physical depth
                            background: isNS 
                                ? 'linear-gradient(135deg, #4A5568 0%, #363d4a 100%)' 
                                : 'linear-gradient(135deg, #2D3748 0%, #1a202c 100%)',
                            // Highlight border if active, otherwise standard
                            border: isOpen ? '1.5px solid #FFD700' : '0.5px solid rgb(173, 173, 173)', 
                            borderRadius: 2,
                            // Added a left-side shadow to distinguish from the card underneath
                            boxShadow: '-2px 0 4px rgba(0,0,0,0.4), 1px 0 3px rgba(0,0,0,0.5)',
                            transform: isOpen ? 'translateY(-2px)' : 'none',
                            transition: 'transform 0.1s'
                        }} />

                        {isOpen && (
                            <div style={{
                                position: 'absolute', 
                                bottom: '45px', 
                                // SMART ANCHORING: Shift right if it's an early trick, shift left if it's late
                                // left: i < 3 ? '0%' : (i > 9 ? 'auto' : '50%'),
                                // right: i > 9 ? '0%' : 'auto',
                                transform: i < 3 ? `translateX(-${19 + i * 12}px)` : 'translateX(-50%)',
                                background: '#0D0E15', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8,
                                width: 110, height: 110, display: 'flex', justifyContent: 'center', alignItems: 'center',
                                zIndex: 9999999, // Extreme Z-Index to stay above everything
                                boxShadow: '0 10px 30px rgba(0,0,0,0.9)'
                            }}>
                                {trick.cards.map((play, ci) => {
                                    const pos = {
                                        N: { transform: 'translate(0px, -24px)' },
                                        S: { transform: 'translate(0px, 24px)' },
                                        W: { transform: 'translate(-26px, 4px)' },
                                        E: { transform: 'translate(26px, -4px)' }
                                    }[play.seat];

                                    return (
                                        <div key={ci} style={{
                                            position: 'absolute', 
                                            ...pos, 
                                            zIndex: ci,
                                            animation: `trickPop 0.25s ease-out ${ci * 0.1}s both`,
                                            // ADDED FOR DEPTH:
                                            borderRadius: 4, // Matches PlayingCard radius
                                            border: '0.5px solid rgb(178, 178, 178)', // Subtle definition border
                                            boxShadow: '2px 4px 8px rgba(0,0,0,0.5), 0 0 2px rgba(255,255,255,0.1)' // Outer drop shadow + faint top highlight
                                        }}>
                                            <PlayingCard card={play.card} />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
            <style>{`
                @keyframes trickPop {
                    0% { opacity: 0; scale: 0.6; }
                    100% { opacity: 1; scale: 1; }
                }
            `}</style>
        </div>
    );
};

const DDTableDisplay = ({ tableData }) => {
    if (!tableData) return null;

    const strains = ['C', 'D', 'H', 'S', 'NT'];
    const players = ['N', 'S', 'E', 'W'];

    return (
        <div style={{ 
            background: 'rgba(65, 65, 65, 0.27)', 
            padding: '8px', 
            borderRadius: 8, 
            border: '1px solid rgba(255, 255, 255, 0.37)', 
            width: 'max-content',
            boxShadow: '0 4px 12px rgba(0,0,0,0.)' 
        }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 11, textAlign: 'center' }}>
                <thead style={{background: 'rgba(65, 65, 65, 0.27)'}}>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.35)' }}>
                        <th style={{ padding: '2px 6px' }}></th>
                        {strains.map(s => (
                            <th key={s} style={{ padding: '2px 6px', color: (s === 'H' || s === 'D') ? '#FF3366' : '#fff' }}>
                                {SUIT_SYMBOLS[s] || 'NT'}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {players.map(p => (
                        <tr key={p} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <td style={{ padding: '2px 6px', fontWeight: 'bold', fontSize: 11, color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' }}>{p}</td>
                            {strains.map(s => {
                                const tricks = tableData[`${p}_${s}`];
                                return (
                                    <td key={`${p}_${s}`} style={{ padding: '2px 6px', color: '#fff', fontSize: 11 }}>
                                        {tricks !== undefined ? tricks : '-'}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const RoomAnalyzer = ({ 
    details, roomName, contractStr, rawScore, isActive,
    boardNum, players, aliases, colors, 
    displayMode, ddTableData, showDdTable, isFullscreen, scale = 1
}) => {


    // TODOX: remove useEffect and render/prevDeps
    
    const vulData = getVulnerability(boardNum);
    
    // --- 1. STATE & REFS ---
    // Generate a unique cache key based on the board, room, and physical cards dealt!
    const stateKey = `ha_${boardNum}_${roomName}_${details?.hands?.substring(0, 15) || 'default'}`;

    const [step, setStep] = usePersistentState(`${stateKey}_step`, 0);
    const [customPlaySequence, setCustomPlaySequence] = usePersistentState(`${stateKey}_customPlay`, []);
    const [isEditingPlay, setIsEditingPlay] = usePersistentState(`${stateKey}_isEditing`, false);
    const [deviationStep, setDeviationStep] = usePersistentState(`${stateKey}_deviation`, null);
    
    // RESTORED: Contract Editing State
    const [customContractStr, setCustomContractStr] = usePersistentState(`${stateKey}_customContract`, null);
    const [isEditingContract, setIsEditingContract] = useState(false);
    const [tempContractEdit, setTempContractEdit] = useState('');

    // NEW: Auction Editing State
    const [customBiddingStr, setCustomBiddingStr] = usePersistentState(`${stateKey}_customBidding`, null);
    const [isEditingAuction, setIsEditingAuction] = useState(false);
    const [tempAuctionArr, setTempAuctionArr] = useState([]);
    const [tempAuctionLvl, setTempAuctionLvl] = useState(null);
    const tapeRef = useRef(null);
    const [ddTrigger, setDdTrigger] = useState(0);

    // Auto-scroll the bidding tape
    useEffect(() => { if (tapeRef.current) tapeRef.current.scrollLeft = tapeRef.current.scrollWidth; }, [tempAuctionArr, isEditingAuction]);

    // Hidden Hands State
    const [hiddenRegions, setHiddenRegions] = usePersistentState(`${stateKey}_hiddenRegions`, { N: false, S: false, E: false, W: false });
    const [flippedCards, setFlippedCards] = usePersistentState(`${stateKey}_flippedCards`, {});

    const [auctionExpanded, setAuctionExpanded] = useState(false);
    const [rawDdResults, setRawDdResults] = useState({});
    const [ddActive, setDdActive] = useState(false);

    const showFullAuction = true;
    const hideAuctionInPlay = true;
    const displayModeOverride = 'visual';

// When details.play changes (new data loaded), reset the custom sequence

    // const [ddTable, setDdTable] = useState(null);
    // const [showDdTable, setShowDdTable] = useState(false);
    const containerRef = useRef(null);
    const workerRef = useRef(null);

    const retroWorkerRef = useRef(null);
    const retroQueueRef = useRef([]); // Holds cards waiting to be evaluated
    const retroActiveRef = useRef(null);
    const clickTimeoutRef = useRef(null);
    

    // --- 2. DATA PARSING ---
    const initialHandsData = useMemo(() => reconstructHands(details?.hands), [details?.hands]);
    
    const activeBidding = useMemo(() => {
        if (customBiddingStr !== null) {
            // FIX: If the user deleted all bids, respect the empty array. Do NOT fall back!
            if (customBiddingStr.trim() === '') return []; 
            return customBiddingStr.split(',').map(s => s.trim()).filter(Boolean);
        }
        return details?.bidding || [];
    }, [customBiddingStr, details?.bidding]);

    const activeAlerts = useMemo(() => {
        const originalBids = (details?.bidding || []).join(', ');
        
        // FIX: Only wipe out alerts if the user ACTUALLY changed the auction. 
        // If localStorage just has a cached copy of the original auction, keep the alerts!
        if (customBiddingStr !== null && customBiddingStr !== originalBids) {
            return activeBidding.map(() => ''); 
        }
        return details?.alerts || [];
    }, [customBiddingStr, details?.alerts, details?.bidding, activeBidding]);

    const originalCData = useMemo(() => parseContractStr(contractStr), [contractStr]);

    const cData = useMemo(() => {
        if (customContractStr !== null) return parseContractStr(customContractStr);
        if (customBiddingStr !== null) {
            const { isAuctionOver } = getAuctionState(activeBidding);
            if (isAuctionOver) return getContractFromAuction(activeBidding, initialHandsData.dealer);
        }
        return originalCData;
    }, [originalCData, customContractStr, customBiddingStr, activeBidding, initialHandsData.dealer]);

    const totalBids = activeBidding.length;
    const isFullAuctionOver = getAuctionState(activeBidding).isAuctionOver;
    // const isFullAuctionOver = getAuctionState(activeBidding).isAuctionOver;
    
    // PHYSICS CHECK: Did the Trump suit or the Declarer change?
    const physicsChanged = cData.suit !== originalCData.suit || cData.declarer !== originalCData.declarer;
    
    // If physics changed and we haven't manually played a card yet, the old timeline is completely invalid!
    const effectivePlaySequence = (physicsChanged && !isEditingPlay) ? [] : customPlaySequence;
    const totalPlays = effectivePlaySequence.length;
    // const playSequence = details?.play || [];

    // const totalPlays = customPlaySequence.length;
    
    // FIX: Ignore the original "claim" flag if we are exploring a hypothetical timeline
    // FIX: Ignore the original "claim" flag if we are exploring a hypothetical timeline
    // const hasEndScreen = (isEditingPlay || customBiddingStr !== null || customContractStr !== null) ? (totalPlays === 52) : (details?.claim || totalPlays === 52);
    const isTimelineForked = isEditingPlay || customBiddingStr !== null || customContractStr !== null;
    const hasEndScreen = isTimelineForked 
        ? (customPlaySequence.length === 52) // Only show end screen if we actually played 52 custom cards
        : (details?.claim || details?.play?.length === 52);

    // UNIFIED TIMELINE: 'step' maps 1:1 across both auction and play for scrubbing
    const currentBids = activeBidding.slice(0, Math.min(step, totalBids));
    const currentAlerts = activeAlerts.slice(0, Math.min(step, totalBids));
    const isPlayPhase = step >= totalBids;
    const isLeadPhase = step === totalBids && isFullAuctionOver;
    
    const playStep = Math.min(Math.max(0, step - totalBids), totalPlays);
    const { isAuctionOver } = getAuctionState(currentBids);

    const [parScore, setParScore] = useState(null);
    
    // const currentBids = showFullAuction ? (details?.bidding || []) : (details?.bidding || []).slice(0, Math.min(step, totalBids));
    // const isPlayPhase = showFullAuction ? true : step >= totalBids;
    // const playStep = showFullAuction ? step : Math.max(0, step - totalBids);

    const WIN_COLOR = '#6cbd8c'; // Softer green
    const LOSE_COLOR = '#ed7666'; // Softer red
    const TIE_COLOR = '#6B7280';  // Grey for zero
    const handleBadgeClick = (seat) => {
        setHiddenRegions(prev => ({ ...prev, [seat]: !prev[seat] }));
    };

    const handleCardClick = (card, seat) => {
        if (!hiddenRegions[seat]) {
            // 1. Hand is visible: Instant play, absolutely no delay!
            handleCardPlay(card, seat);
            return;
        }

        // 2. Hand is hidden: Determine if it's a peek (single) or play (double)
        if (clickTimeoutRef.current) {
            // Second click detected! Cancel the flip and play the card instead.
            clearTimeout(clickTimeoutRef.current);
            clickTimeoutRef.current = null;
            handleCardPlay(card, seat);
        } else {
            // First click detected! Start the 250ms countdown.
            clickTimeoutRef.current = setTimeout(() => {
                // If the timer finishes without a second click, it's just a peek. Flip it!
                setFlippedCards(prev => ({ ...prev, [card.id]: !prev[card.id] }));
                clickTimeoutRef.current = null;
            }, 150); 
        }
    };

    const handleCardPlay = (card, seat) => {
        // 1. Only allow the player whose turn it is to play
        if (seat !== tableState.nextToPlay || !isPlayPhase) return;

        // 2. Enforce Bridge Rules (Must follow suit if possible)
        const leadSuit = (tableState.currentTrick.length > 0 && tableState.currentTrick.length < 4) ? tableState.currentTrick[0].card.suit : null;
        const unplayedCards = tableState.hands[seat].filter(c => !c.isPlayed);
        const hasLeadSuit = leadSuit && unplayedCards.some(c => c.suit === leadSuit);
        
        if (hasLeadSuit && card.suit !== leadSuit) {
            return; // Illegal play, ignore click
        }

        // 3. Fork the Timeline
        if (!isEditingPlay) {
            setIsEditingPlay(true);
            if (deviationStep === null) setDeviationStep(step); // Save the exact point in time we deviated
        }
        
        // Truncate the EFFECTIVE sequence to the current step, then append the clicked card
        const newSequence = [...effectivePlaySequence.slice(0, playStep), card.id];
        setCustomPlaySequence(newSequence);
        
        // 4. Wipe the DD Cache to prevent Option A "stale ghost data" bugs!
        if (mappedDdScores[card.id] !== undefined) {
            ddCacheRef.current[card.id] = mappedDdScores[card.id];
        }
        setRawDdResults({});
        
        // Advance the step automatically (safely accounting for auction offset)
        setStep(totalBids + playStep + 1);
        // setStep(showFullAuction ? playStep + 1 : totalBids + playStep + 1);
    };

    const evaluateTrick = (trickCards, trumpSuit) => {
        if (trickCards.length === 0) return null;
        let winningCard = trickCards[0];

        for (let i = 1; i < trickCards.length; i++) {
            const c = trickCards[i];
            const wSuit = winningCard.card.suit;
            const cSuit = c.card.suit;
            const cRank = getRankValue(c.card.rank);
            const wRank = getRankValue(winningCard.card.rank);

            // Trump beats non-trump. Otherwise, highest rank of the leading suit wins.
            if (cSuit === trumpSuit && wSuit !== trumpSuit) {
                winningCard = c; 
            } else if (cSuit === wSuit && cRank > wRank) {
                winningCard = c; 
            }
        }
        return winningCard.seat;
    };

    const tableState = useMemo(() => {
        // Deep clone
        let hands = { 
            S: initialHandsData.S.map(c => ({...c, isPlayed: false})), 
            W: initialHandsData.W.map(c => ({...c, isPlayed: false})), 
            N: initialHandsData.N.map(c => ({...c, isPlayed: false})), 
            E: initialHandsData.E.map(c => ({...c, isPlayed: false})) 
        };

        let trickHistory = [];
        
        let currentTrick = []; 
        let lastTrick = []; // Holds the 4 cards of the *previous* trick temporarily
        let tricksWon = { NS: 0, EW: 0 };
        
        const declarerIdx = SEATS.indexOf(cData.declarer);
        let currentLeader = declarerIdx !== -1 ? SEATS[(declarerIdx + 1) % 4] : 'W'; 
        let trickWinner = null;

        for (let i = 0; i < playStep && i < effectivePlaySequence.length; i++) {
            const playedCardId = effectivePlaySequence[i].toUpperCase().replace(/\s/g, '').replace('10', 'T');
            
            // If the current trick is full (4 cards), and a 5th is played, 
            // WE NOW clear it to start the next trick.
            if (currentTrick.length === 4) {
                trickHistory.push({ winner: evaluateTrick(currentTrick, cData.trump), cards: [...currentTrick] });
                lastTrick = [...currentTrick];
                trickWinner = evaluateTrick(currentTrick, cData.trump);
                if (trickWinner === 'N' || trickWinner === 'S') tricksWon.NS += 1; else tricksWon.EW += 1;
                currentLeader = trickWinner; 
                currentTrick = []; // Clear for the new card
            }

            SEATS.forEach(seat => {
                const cardObj = hands[seat].find(c => c.id === playedCardId);
                if (cardObj && !cardObj.isPlayed) {
                    cardObj.isPlayed = true;
                    currentTrick.push({ seat: seat, card: cardObj });
                }
            });
        }
        
        // If we finished a loop and exactly 4 cards are sitting there, 
        // figure out who won it for UI display purposes, but DON'T clear it yet.
        if (currentTrick.length === 4) {
            trickWinner = evaluateTrick(currentTrick, cData.trump);
        }

        const nextToPlay = currentTrick.length === 0 || currentTrick.length === 4 ? (trickWinner || currentLeader) : SEATS[(SEATS.indexOf(currentLeader) + currentTrick.length) % 4];
        
        return { hands, currentTrick, lastTrick, tricksWon, currentLeader, nextToPlay, trickWinner, trickHistory };
    }, [initialHandsData, effectivePlaySequence, playStep, cData]);

    const getHistoricalState = (targetStep) => {
        let hHands = { 
            S: initialHandsData.S.map(c => ({...c, isPlayed: false})), 
            W: initialHandsData.W.map(c => ({...c, isPlayed: false})), 
            N: initialHandsData.N.map(c => ({...c, isPlayed: false})), 
            E: initialHandsData.E.map(c => ({...c, isPlayed: false})) 
        };
        let hTrick = [];
        let hWon = { NS: 0, EW: 0 };
        const declarerIdx = SEATS.indexOf(cData.declarer);
        let hLeader = declarerIdx !== -1 ? SEATS[(declarerIdx + 1) % 4] : 'W'; 
        let hWinner = null;

        for (let i = 0; i < targetStep; i++) {
            const pid = effectivePlaySequence[i].toUpperCase().replace(/\s/g, '').replace('10', 'T');
            if (hTrick.length === 4) {
                hWinner = evaluateTrick(hTrick, cData.trump);
                if (hWinner === 'N' || hWinner === 'S') hWon.NS += 1; else hWon.EW += 1;
                hLeader = hWinner; 
                hTrick = []; 
            }
            SEATS.forEach(seat => {
                const c = hHands[seat].find(x => x.id === pid);
                if (c && !c.isPlayed) { c.isPlayed = true; hTrick.push({ seat, card: c }); }
            });
        }
        if (hTrick.length === 4) hWinner = evaluateTrick(hTrick, cData.trump);
        
        const hNextToPlay = hTrick.length === 0 || hTrick.length === 4 ? (hWinner || hLeader) : SEATS[(SEATS.indexOf(hLeader) + hTrick.length) % 4];
        
        // Build the PBN just like the main thread does
        const trickIds = hTrick.length === 4 ? [] : hTrick.map(t => t.card.id);
        const formatHand = (arr) => {
            const valid = arr.filter(c => !c.isPlayed || trickIds.includes(c.id));
            const getS = (s) => valid.filter(c => c.suit === s).map(c => c.rank === '10' ? 'T' : c.rank).join('');
            return `${getS('S')}.${getS('H')}.${getS('D')}.${getS('C')}`;
        };
        const pbnStr = `N:${formatHand(hHands.N)} ${formatHand(hHands.E)} ${formatHand(hHands.S)} ${formatHand(hHands.W)}`;
        
        return { pbnStr, hTrick, hWon, hNextToPlay, hLeader, hWinner };
    };

    // --- 3. EFFECTS ---

    const prevStepRef = useRef(step);
    const [isForward, setIsForward] = useState(true);

    // AUTO-TOGGLE AUCTION EDITOR
    useEffect(() => {
        if (playStep === 0 && !isAuctionOver) {
            // 1. Auto-open if the auction is incomplete and we are in the bidding phase
            if (!isEditingAuction) {
                if (customBiddingStr === null) setCustomBiddingStr(activeBidding.join(', ')); 
                setTempAuctionLvl(null); 
                setIsEditingAuction(true); 
                setIsEditingPlay(false);
                if (deviationStep === null) setDeviationStep(step);
            }
        } else if (playStep > 0 && isEditingAuction) {
            // 2. Auto-close ONLY if we scrub forward into the card play phase!
            // (Leaving it alone if playStep === 0 and isAuctionOver === true allows the manual Edit button to work)
            setIsEditingAuction(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playStep, isAuctionOver]);

    useEffect(() => {
        const goingForward = step >= prevStepRef.current;
        setIsForward(goingForward);
        
        // FIX: If we scrub backwards into the auction phase, clear the DD cache so ghost numbers vanish!
        if (!goingForward && step <= totalBids) {
            ddCacheRef.current = {};
            setRawDdResults({});
        }
        
        prevStepRef.current = step;
    }, [step, totalBids]);

    useEffect(() => {
        setCustomPlaySequence(details?.play || []);
        setIsEditingPlay(false);
        // Default to the end of the auction when first loading a room
        setStep(details?.bidding?.length || 0);
    }, [details?.play, details?.bidding]);
    
    // Click-outside listener for Bidding Box
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setAuctionExpanded(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // BULLETPROOF WORKER INITIALIZATION
    const processNextRetro = () => {
        if (!retroWorkerRef.current || retroActiveRef.current || retroQueueRef.current.length === 0) return;
        
        // Pop the next card requested from the queue
        const nextJob = retroQueueRef.current.shift();
        retroActiveRef.current = nextJob;

        const trumpInt = getTrumpInt(cData.trump);
        const isTrickFull = nextJob.hTrick.length === 4;
        const firstInt = isTrickFull ? getSeatInt(nextJob.hWinner) : getSeatInt(nextJob.hLeader);
        const trickStr = isTrickFull ? "" : nextJob.hTrick.map(play => play.card.id).join(',');

        retroWorkerRef.current.postMessage({
            trump: trumpInt, first: firstInt, pbn: nextJob.pbnStr, currentTrick: trickStr
        });
    };

    const currentPbn = useMemo(() => {
        if (!isActive || !isPlayPhase && !isLeadPhase) return null;
        return convertToPBN(tableState.hands, tableState.currentTrick);
    }, [tableState.hands, tableState.currentTrick, isPlayPhase, isLeadPhase]);

    useEffect(() => {
        if (isActive && !workerRef.current) {
            // Main UI Worker
            workerRef.current = new Worker(`/ddsWorker.js?v=${DDS_WASM_VERSION}`);
            workerRef.current.onmessage = (e) => {
                if (e.data.type === 'RESULT') setRawDdResults(e.data.data);
            };

            // Background Historical Worker
            retroWorkerRef.current = new Worker(`/ddsWorker.js?v=${DDS_WASM_VERSION}`);
            retroWorkerRef.current.onmessage = (e) => {
                if (e.data.type === 'RESULT' && retroActiveRef.current) {
                    const job = retroActiveRef.current;
                    const rawRetro = e.data.data;
                    
                    // Do the exact same math as mappedDdScores, but using historical variables
                    const target = Number(cData.level) + 6;
                    const declarerSide = (cData.declarer === 'N' || cData.declarer === 'S') ? 'NS' : 'EW';
                    const targetSide = (job.hNextToPlay === 'N' || job.hNextToPlay === 'S') ? 'NS' : 'EW';
                    
                    const completedTricks = Math.floor(job.step / 4);
                    const remainingTricks = 13 - completedTricks;
                    const isTrickFull = job.hTrick.length === 4;
                    
                    const effWon = {
                        NS: job.hWon.NS + (isTrickFull && (job.hWinner === 'N' || job.hWinner === 'S') ? 1 : 0),
                        EW: job.hWon.EW + (isTrickFull && (job.hWinner === 'E' || job.hWinner === 'W') ? 1 : 0)
                    };

                    const absoluteTricks = rawRetro[job.cardId];
                    if (absoluteTricks !== undefined) {
                        const tricksNum = Number(absoluteTricks);
                        let finalTotal = targetSide === declarerSide 
                            ? effWon[declarerSide] + tricksNum 
                            : effWon[declarerSide] + (remainingTricks - tricksNum);
                            
                        // Silently inject into the UI Cache!
                        ddCacheRef.current[job.cardId] = finalTotal - target;
                        // Force a tiny re-render so the pill appears immediately
                        setRawDdResults(prev => ({...prev})); 
                    }

                    // Clear active job and trigger the next one
                    retroActiveRef.current = null;
                    processNextRetro();
                }
            };
        }
        return () => {
            // FIX: Unconditionally terminate workers on unmount to prevent React Router from crashing!
            if (workerRef.current) { workerRef.current.terminate(); workerRef.current = null; }
            if (retroWorkerRef.current) { retroWorkerRef.current.terminate(); retroWorkerRef.current = null; }
        };
    }, [isActive, cData]);

    // TRIGGER WORKER ON TRICK START

    // const lastSentPbn = useRef("");

    useEffect(() => {

        if (!isActive || !ddActive || cData.isPass || (!isPlayPhase || !isAuctionOver)) {
            return;
        }

        const pbnStr = convertToPBN(tableState.hands, tableState.currentTrick);
        // if (pbnStr === lastSentPbn.current) return;
        // lastSentPbn.current = pbnStr;

        workerRef.current?.postMessage({
            trump: getTrumpInt(cData.trump),
            first: getSeatInt(tableState.currentTrick.length === 4 ? tableState.trickWinner : tableState.currentLeader),
            pbn: pbnStr,
            currentTrick: tableState.currentTrick.length === 4 ? "" : tableState.currentTrick.map(p => p.card.id).join(',')
        });

    }, [isActive, ddActive, isPlayPhase, tableState.hands, tableState.currentTrick, cData]);

    // useEffect(() => {
    //     if (!isActive || !ddActive || cData.isPass || !currentPbn) {
    //         setRawDdResults({}); // Clear if we aren't active
    //         return;
    //     }

    //     const trumpInt = getTrumpInt(cData.trump);
    //     const isTrickFull = tableState.currentTrick.length === 4;
    //     const firstInt = isTrickFull ? getSeatInt(tableState.trickWinner) : getSeatInt(tableState.currentLeader);
    //     const trickStr = isTrickFull ? "" : tableState.currentTrick.map(play => play.card.id).join(',');

    //     // firing the message
    //     workerRef.current?.postMessage({
    //         trump: trumpInt,
    //         first: firstInt,
    //         pbn: currentPbn,
    //         currentTrick: trickStr
    //     });

    // // REMOVED tableState.hands and tableState.currentTrick from here
    // // ADDED currentPbn as the primary driver
    // }, [isActive, ddActive, currentPbn, cData.trump, cData.declarer, ddTrigger]);
    // useEffect(() => {
    //     // 1. SAFE WIPE: Instantly clears old scores so React never calculates ghost numbers
    //     setRawDdResults({});

    //     // 2. DIAGRAM UNLOCK: Removed !isPlayPhase so DD runs during the auction phase
    //     if (!isActive || !ddActive || cData.isPass || (!isPlayPhase && !isLeadPhase)) {
    //         return;
    //     }
        
    //     const pbnStr = convertToPBN(tableState.hands, tableState.currentTrick);
    //     if (pbnStr) {
    //         const trumpInt = getTrumpInt(cData.trump);

    //         const isTrickFull = tableState.currentTrick.length === 4;
    //         const firstInt = isTrickFull ? getSeatInt(tableState.trickWinner) : getSeatInt(tableState.currentLeader);
    //         const trickStr = isTrickFull ? "" : tableState.currentTrick.map(play => play.card.id).join(',');

    //         // console.log(`📡 SENDING TO WASM -> Trump: ${trumpInt} | First: ${firstInt} | Trick: ${trickStr} | PBN:`, pbnStr);

    //         workerRef.current?.postMessage({
    //             trump: trumpInt,
    //             first: firstInt,
    //             pbn: pbnStr,
    //             currentTrick: trickStr
    //         });
    //     }
    // }, [isActive, ddActive, isPlayPhase, tableState.hands, tableState.currentTrick, tableState.currentLeader, tableState.trickWinner, cData, ddTrigger, isFullAuctionOver]);

    useEffect(() => {
        // If DD is off, or no cards are on the table, do nothing
        if (!ddActive || !isActive || tableState.currentTrick.length === 0) return;
        
        // Grab the most recently played card currently sitting on the table
        const lastPlayIdx = tableState.currentTrick.length - 1;
        const lastPlay = tableState.currentTrick[lastPlayIdx];
        
        // If it's missing from the cache, we need to secretly fetch it!
        if (ddCacheRef.current[lastPlay.card.id] === undefined) {
            
            // The exact moment BEFORE this card was played
            const targetStep = playStep - 1; 
            
            // Prevent spamming the queue if we are already fetching it
            const inQueue = retroQueueRef.current.some(job => job.cardId === lastPlay.card.id);
            const isProcessing = retroActiveRef.current?.cardId === lastPlay.card.id;
            
            if (!inQueue && !isProcessing) {
                // Spin up the time machine
                const historicalState = getHistoricalState(targetStep);
                
                // Add to the background worker queue
                retroQueueRef.current.push({
                    cardId: lastPlay.card.id,
                    step: targetStep,
                    ...historicalState
                });
                
                // Fire the engine!
                processNextRetro();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ddActive, isActive, tableState.currentTrick, playStep]);

    // --- 4. TRANSLATE DD RESULTS ---
    const mappedDdScores = useMemo(() => {
        if (!isPlayPhase || !rawDdResults || Object.keys(rawDdResults).length === 0 || !cData || cData.isPass) return {};
        const scores = {};
        
        const target = Number(cData.level) + 6;
        const declarerSide = (cData.declarer === 'N' || cData.declarer === 'S') ? 'NS' : 'EW';
        
        // 1. PERSPECTIVE: Calculate ONLY for the player whose turn it currently is
        const targetSeat = tableState.nextToPlay;
        const targetSide = (targetSeat === 'N' || targetSeat === 'S') ? 'NS' : 'EW';

        // 2. IDENTIFY LEGAL CARDS: Must follow suit if possible
        const unplayedTargetCards = tableState.hands[targetSeat].filter(c => !c.isPlayed);
        const leadSuit = (tableState.currentTrick.length > 0 && tableState.currentTrick.length < 4) ? tableState.currentTrick[0].card.suit : null;
        const hasLeadSuit = leadSuit && unplayedTargetCards.some(c => c.suit === leadSuit);
        
        // Array of IDs for cards that are legally allowed to be played right now
        const legalCardIds = hasLeadSuit 
            ? unplayedTargetCards.filter(c => c.suit === leadSuit).map(c => c.id) 
            : unplayedTargetCards.map(c => c.id);

        // 3. TOUCHING CARDS FIX: Group by absolute unplayed sequence
        const filledResults = { ...rawDdResults };
        
        // Get ALL unplayed cards across the entire table to accurately define "touching"
        const allUnplayed = [];
        SEATS.forEach(s => {
            tableState.hands[s].forEach(c => {
                if (!c.isPlayed) allUnplayed.push({ ...c, seat: s });
            });
        });

        SUITS.forEach(suit => {
            // Sort all remaining cards in this suit globally from highest to lowest
            const suitUnplayed = allUnplayed.filter(c => c.suit === suit).sort((a, b) => getRankValue(b.rank) - getRankValue(a.rank));
            
            // Forward pass: Copy scores down to touching cards
            let currentScore = null;
            for (let i = 0; i < suitUnplayed.length; i++) {
                const c = suitUnplayed[i];
                if (c.seat !== targetSeat) {
                    currentScore = null; // Sequence broken by another player's card!
                } else {
                    if (filledResults[c.id] !== undefined) currentScore = filledResults[c.id];
                    else if (currentScore !== null) filledResults[c.id] = currentScore;
                }
            }
            
            // Backward pass: Copy scores up (in case DDS returned the bottom of the sequence)
            currentScore = null;
            for (let i = suitUnplayed.length - 1; i >= 0; i--) {
                const c = suitUnplayed[i];
                if (c.seat !== targetSeat) {
                    currentScore = null; // Sequence broken by another player's card!
                } else {
                    if (rawDdResults[c.id] !== undefined) currentScore = rawDdResults[c.id];
                    else if (currentScore !== null && filledResults[c.id] === undefined) filledResults[c.id] = currentScore;
                }
            }
        });

        // 4. MATHEMATICAL TRANSLATION (Applying only to legal cards)
        const isTrickFull = tableState.currentTrick.length === 4;

        // Because we stripped full tricks from the PBN, the DD solver evaluates
        // based on the number of tricks completely finished.
        const completedTricks = Math.floor(playStep / 4);
        const remainingTricks = 13 - completedTricks;

        // If the trick is exactly 4 cards, tableState.tricksWon hasn't swept it into the 
        // official tally yet, but the DD solver assumes it's already in the bank. We must manually add it.
        const effectiveTricksWon = {
            NS: tableState.tricksWon.NS + (isTrickFull && (tableState.trickWinner === 'N' || tableState.trickWinner === 'S') ? 1 : 0),
            EW: tableState.tricksWon.EW + (isTrickFull && (tableState.trickWinner === 'E' || tableState.trickWinner === 'W') ? 1 : 0)
        };

        legalCardIds.forEach(cardId => {
            const absoluteTricks = filledResults[cardId];
            if (absoluteTricks !== undefined) {
                const tricksNum = Number(absoluteTricks);
                let declarerFinalTotal;

                if (targetSide === declarerSide) {
                    // Declarer is playing: Final total is past tricks + future tricks
                    declarerFinalTotal = effectiveTricksWon[declarerSide] + tricksNum;
                } else {
                    // Defender is playing: Declarer's final total is declarer's past tricks + declarer's future tricks
                    // (Defender's future tricks = tricksNum. Declarer's future tricks = remainingTricks - tricksNum)
                    const remainingForDeclarer = remainingTricks - tricksNum;
                    declarerFinalTotal = effectiveTricksWon[declarerSide] + remainingForDeclarer;
                }

                scores[cardId] = declarerFinalTotal - target;
            }
        });

        // console.log(`🔍 DD CHECK (Trick ${Math.floor(playStep / 4) + 1}) | Seat: ${targetSeat}`, {
        //     legalCardsAllowed: legalCardIds,
        //     rawAbsoluteTricksFromSolver: filledResults,
        //     finalBadgeScores: scores,
        //     targetNeeded: target,
        // });
        
        return scores;
    }, [rawDdResults, cData, tableState, playStep]);
    // }, [rawDdResults, cData.level, cData.suit, cData.declarer, tableState.nextToPlay, tableState.currentTrick.length]);

    const ddCacheRef = useRef({});
    useEffect(() => {
        if (Object.keys(mappedDdScores).length > 0) {
            ddCacheRef.current = { ...ddCacheRef.current, ...mappedDdScores };
        }
    }, [mappedDdScores]);

    // useEffect(() => {
    //     if (playStep === 0 && !isAuctionOver){
    //         if (!isEditingAuction){
    //             if (customBiddingStr === null) setCustomBiddingStr(activeBidding.join(', ')); 
    //             setTempAuctionLvl(null); setIsEditingAuction(true); 
    //             // setStep(activeBidding.length);
    //             setIsEditingPlay(false);
    //             if (deviationStep === null) setDeviationStep(step);
    //         }
    //     }else{
    //         setIsEditingAuction(false);
    //     }
    // }, [playStep, isAuctionOver]);

    // --- 5. EARLY RETURNS ---
    if (!details || !details.hands) return <div style={{padding: 40, color: '#555', textAlign: 'center', fontSize: 12, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 8, flex: 1}}>No data available for {roomName} Room.</div>;

    const pNames = {
        N: aliases?.N || players[2] || 'North', 
        S: aliases?.S || players[0] || 'South', 
        E: aliases?.E || players[3] || 'East', 
        W: aliases?.W || players[1] || 'West' 
    };

    // const maxStep = showFullAuction ? totalPlays : totalBids + totalPlays;
    // const { isAuctionOver: isFullAuctionOver } = getAuctionState(activeBidding);
    
    const maxStep = isFullAuctionOver ? (totalBids + totalPlays + (hasEndScreen ? 1 : 0)) : totalBids;
    // If the auction isn't finished, strictly cap the timeline at the end of the bids!
    // const maxStep = isFullAuctionOver ? (totalBids + totalPlays + (hasEndScreen ? 1 : 0)) : totalBids;

    const dealerIdx = SEATS.indexOf(initialHandsData.dealer) !== -1 ? SEATS.indexOf(initialHandsData.dealer) : 3;
    const currentAuctionTurn = SEATS[(dealerIdx + currentBids.length) % 4];
    
    
    // Bidding phase is active ONLY if the auction isn't over yet
    const isBiddingPhase = (!isPlayPhase || playStep === 0) && !isAuctionOver;
    
    const activeSeat = isBiddingPhase 
        ? currentAuctionTurn
        : ((step === maxStep && hasEndScreen) ? null : tableState.nextToPlay);

    

    return (
        <div ref={containerRef} style={{ 
            background: '#11131A', padding: '15px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)', 
            position: 'relative', display: 'flex', flexDirection: 'column', 
            // FIX: Lock to a rigid 600x550 box in fullscreen so flexbox cannot stretch it. It will only scale!
            width: isFullscreen ? 600 : '100%', 
            height: isFullscreen ? 550 : '100%', 
            zoom: scale 
        }}>
            
            <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                
                {/* Clean, fixed Top-Left Info Corner */}
                {/* <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 10, width: 100 }}>
                    <div style={{ background: 'rgba(0,0,0,0.6)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', textAlign: 'center' }}>
                        <div style={{ background: 'rgba(255,255,255,0.05)', color: '#A0AABF', fontSize: 9, fontWeight: 'bold', padding: '4px', textTransform: 'uppercase', letterSpacing: 1 }}>{vulData.label} Vul</div>
                        <div style={{ padding: '6px' }}>
                            {cData.isPass ? (
                                <div style={{ fontSize: 14, fontWeight: 'bold', color: '#fff' }}>Pass</div>
                            ) : (
                                <>
                                    <div style={{ fontSize: 15, fontWeight: 'bold', color: '#fff' }}>{cData.level}<span style={{color: SUIT_COLORS[cData.suit] || '#fff'}}>{SUIT_SYMBOLS[cData.suit] || cData.suit}</span>{cData.dbl}</div>
                                    <div style={{ fontSize: 11, color: '#A0AABF' }}>By {cData.declarer}</div>
                                </>
                            )}
                        </div>
                    </div>
                </div> */}

                <div style={{ color: '#E0E0E0', fontSize: 13, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 15, display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ color: '#fff' }}>
                        {roomName}:
                    </span> 
                    {/* {formatContract(contractStr)} */}
                </div>
                {/* <div style={{ color: '#E0E0E0', fontSize: 13, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 15 }}>{roomName} Room</div> */}

                {displayModeOverride === 'visual' ? (
                    <div className="ha-table-container">
                        <div className="ha-hand-north">
                            {/* Contract Box */}
                            <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 10, width: 100, pointerEvents: 'auto' }}>
                                <div 
                                    onDoubleClick={() => {
                                        if (!isEditingContract) {
                                            setTempContractEdit(customContractStr !== null ? customContractStr : contractStr);
                                            setIsEditingContract(true);
                                            if (deviationStep === null) setDeviationStep(step);
                                        }
                                    }}
                                    title="Double-click to edit contract"
                                    style={{ 
                                        background: isEditingContract ? '#2D2B3F' : 'rgba(0,0,0,0.6)', 
                                        borderRadius: 8, border: `1px solid ${customContractStr ? '#FFD700' : 'rgba(255,255,255,0.1)'}`, 
                                        overflow: 'hidden', textAlign: 'center', cursor: 'pointer', transition: '0.2s',
                                        boxShadow: customContractStr ? '0 0 10px rgba(255,215,0,0.2)' : 'none'
                                    }}
                                >
                                    <div style={{ background: 'rgba(255,255,255,0.05)', color: '#A0AABF', fontSize: 9, fontWeight: 'bold', padding: '4px', textTransform: 'uppercase', letterSpacing: 1 }}>
                                        {isEditingContract ? 'Edit Contract' : `${vulData.label} Vul`}
                                    </div>
                                    <div style={{ padding: '6px' }}>
                                        {isEditingContract ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                <input 
                                                    autoFocus value={tempContractEdit}
                                                    onChange={(e) => setTempContractEdit(e.target.value.toUpperCase())}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            setCustomContractStr(tempContractEdit);
                                                            setIsEditingContract(false);
                                                            // Wiping the cache forces the DD solver to re-evaluate the rest of the hand using the NEW trump suit!
                                                            ddCacheRef.current = {}; setRawDdResults({});
                                                            setDdTrigger(t => t + 1);
                                                        }
                                                        if (e.key === 'Escape') setIsEditingContract(false);
                                                    }}
                                                    onBlur={() => setIsEditingContract(false)}
                                                    style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #6C5DD3', borderRadius: 4, padding: '2px 4px', fontSize: 13, textAlign: 'center' }}
                                                    placeholder="e.g. 4S S"
                                                />
                                                <div style={{ fontSize: 9, color: '#A0AABF' }}>Press Enter</div>
                                            </div>
                                        ) : cData.isPass ? (
                                            <div style={{ fontSize: 14, fontWeight: 'bold', color: '#fff' }}>Pass</div>
                                        ) : (
                                            <div style={{ fontSize: 15, fontWeight: 'bold', color: '#fff' }}>
                                                {cData.level}
                                                <span style={{color: (cData.suit === 'C' || cData.suit === 'S') ? '#fff' : SUIT_COLORS[cData.suit] || '#fff'}}>
                                                    {SUIT_SYMBOLS[cData.suit] || 'NT'}
                                                </span>
                                                {cData.declarer}{cData.dbl}
                                                {cData.res && customContractStr === null && (
                                                    <span style={{ marginLeft: 4 }}>{cData.res}{cData.tricks}</span>
                                                )}
                                                {customContractStr !== null && <div style={{fontSize: 9, color: '#FFD700', marginTop: 2}}>Custom</div>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {showDdTable && ddTableData && (
                                <div style={{ 
                                    position: 'absolute', top: 0, right: 0, zIndex: 10, 
                                    transform: 'scale(0.8)', transformOrigin: 'top right',
                                    pointerEvents: 'auto' // Re-enable clicks for DD Table
                                }}>
                                    <DDTableDisplay tableData={ddTableData} />
                                </div>
                            )}
                            
                            {/* North Hand Component with Roster Name */}
                            <div style={{ pointerEvents: 'auto' }}>
                                <PlayerHand 
                                    seat="N" pName={pNames.N} realName={aliases?.N} 
                                    isVul={vulData.NS} teamColor={colors.NS} cards={tableState.hands.N} 
                                    ddScores={ddActive ? mappedDdScores : []} trump={cData.trump} 
                                    onCardClick={handleCardClick}
                                    onBadgeClick={handleBadgeClick}
                                    isHiddenRegion={hiddenRegions.N}
                                    flippedCards={flippedCards}
                                    isActiveTurn={activeSeat === 'N'}
                                    isBiddingPhase={isBiddingPhase}
                                    leadSuit={(tableState.currentTrick.length > 0 && tableState.currentTrick.length < 4) ? tableState.currentTrick[0].card.suit : null}
                                />
                            </div>
                        </div>
                        <div className="ha-hand-west">
                            <PlayerHand 
                                    seat="W" pName={pNames.W} realName={aliases?.W} 
                                    isVul={vulData.EW} teamColor={colors.EW} cards={tableState.hands.W} 
                                    ddScores={ddActive ? mappedDdScores : []} trump={cData.trump} 
                                    onCardClick={handleCardClick}
                                    onBadgeClick={handleBadgeClick}
                                    isHiddenRegion={hiddenRegions.W}
                                    flippedCards={flippedCards}
                                    isActiveTurn={activeSeat === 'W'}
                                    isBiddingPhase={isBiddingPhase}
                                    leadSuit={(tableState.currentTrick.length > 0 && tableState.currentTrick.length < 4) ? tableState.currentTrick[0].card.suit : null}
                                />
                        </div>
                        
                        
                        <div className="ha-trick-area">
                            {/* State 1: Auction */}
                            {(!isPlayPhase || playStep === 0 || (isEditingAuction && !isAuctionOver)) && !(step === maxStep && hasEndScreen) ? (
                                <div style={{position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%'}}>
                                    
                                    <BiddingBox bidding={currentBids} alerts={currentAlerts} dealer={initialHandsData.dealer} isExpanded={true} vulData={vulData} />
                                    {/* {customBiddingStr !== null && <div style={{ position: 'absolute', bottom: -15, left: '50%', transform: 'translateX(-50%)', fontSize: 9, color: '#FFD700', background: 'rgba(0,0,0,0.8)', padding: '2px 8px', borderRadius: 4 }}>Custom Auction</div>} */}

                                    {/* BBO-STYLE EDITOR OVERLAY
                                    {isEditingAuction && (() => {
                                        const { lastBidValue, canDouble, canRedouble, isAuctionOver } = getAuctionState(tempAuctionArr);
                                        const suitVals = { 'C':0, 'D':1, 'H':2, 'S':3, 'N':4 };
                                        const addBid = (b) => { setTempAuctionArr(prev => [...prev, b]); setTempAuctionLvl(null); };
                                        const btnBase = { flex: 1, padding: '4px 0', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, fontSize: 12, fontWeight: 'bold', cursor: 'pointer', transition: '0.1s' };
                                        
                                        return (
                                            <div style={{ width: 190, background: '#0D0E15', borderRadius: 8, border: '2px solid #FFD700', padding: '8px', display: 'flex', flexDirection: 'column', gap: 6, boxShadow: '0 8px 30px rgba(0,0,0,1)', zIndex: 1005 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, fontWeight: 'bold', textTransform: 'uppercase', color: '#FFD700' }}>
                                                    <span>Edit Auction</span>
                                                    <span style={{color: isAuctionOver ? '#FF3366' : '#A0AABF'}}>{isAuctionOver ? 'Complete' : 'Your Call'}</span>
                                                </div>
                                                <div ref={tapeRef} className="ha-hide-scroll" style={{ background: '#000', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding: '4px 6px', height: 26, display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto', whiteSpace: 'nowrap' }}>
                                                    {tempAuctionArr.length === 0 && <span style={{color: '#555', fontSize: 10, fontStyle: 'italic'}}>Empty...</span>}
                                                    {tempAuctionArr.map((b, i) => {
                                                        const c = b.split('!')[0].toUpperCase();
                                                        let content = c;
                                                        if (c === 'P') content = <span style={{color: '#9cedc'}}>P</span>;
                                                        else if (c === 'X') content = <span style={{color: '#ed7666'}}>X</span>;
                                                        else if (c === 'XX') content = <span style={{color: '#6C5DD3'}}>XX</span>;
                                                        else {
                                                            const m = c.match(/^([1-7])([CDHSN])/);
                                                            if (m) content = <span>{m[1]}<span style={{color: SUIT_COLORS[m[2]] || '#fff'}}>{SUIT_SYMBOLS[m[2]] || 'NT'}</span></span>;
                                                        }
                                                        return <span key={i} style={{fontSize: 11, fontWeight: 'bold', color: '#fff'}}>{content}</span>;
                                                    })}
                                                </div>

                                                <div style={{ opacity: isAuctionOver ? 0.3 : 1, pointerEvents: isAuctionOver ? 'none' : 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        {[1,2,3,4,5,6,7].map(l => {
                                                            const isLvlDisabled = l * 5 + 4 <= lastBidValue;
                                                            const isSel = tempAuctionLvl === l;
                                                            return <button key={l} disabled={isLvlDisabled} onClick={() => setTempAuctionLvl(isSel ? null : l)} style={{ ...btnBase, background: isSel ? '#FFD700' : '#2D2B3F', color: isSel ? '#000' : '#fff', opacity: isLvlDisabled ? 0.2 : 1 }}>{l}</button>;
                                                        })}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        {['C','D','H','S','N'].map(s => {
                                                            const isSuitDisabled = !tempAuctionLvl || (tempAuctionLvl * 5 + suitVals[s] <= lastBidValue);
                                                            return <button key={s} disabled={isSuitDisabled} onClick={() => addBid(`${tempAuctionLvl}${s}`)} style={{ ...btnBase, fontSize: 14, background: '#2D2B3F', color: SUIT_COLORS[s] || '#fff', opacity: isSuitDisabled ? 0.2 : 1 }}>{SUIT_SYMBOLS[s] || 'NT'}</button>;
                                                        })}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 2 }}>
                                                        <button onClick={() => addBid('P')} style={{ ...btnBase, background: 'rgba(156, 237, 140, 0.15)', color: '#9cedc' }}>Pass</button>
                                                        <button disabled={!canDouble} onClick={() => addBid('X')} style={{ ...btnBase, background: canDouble ? 'rgba(237, 118, 102, 0.15)' : 'rgba(255,255,255,0.05)', color: canDouble ? '#ed7666' : '#555' }}>X</button>
                                                        <button disabled={!canRedouble} onClick={() => addBid('XX')} style={{ ...btnBase, background: canRedouble ? 'rgba(108, 93, 211, 0.15)' : 'rgba(255,255,255,0.05)', color: canRedouble ? '#A395E9' : '#555' }}>XX</button>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                                                    <button onClick={() => { setTempAuctionArr(prev => prev.slice(0, -1)); setTempAuctionLvl(null); }} style={{ ...btnBase, background: '#333', color: '#fff' }}>Undo</button>
                                                    <button onClick={() => setIsEditingAuction(false)} style={{ ...btnBase, background: '#E53E3E', color: '#fff' }}>Cancel</button>
                                                    <button onClick={() => { 
                                                        setCustomBiddingStr(tempAuctionArr.join(', ')); 
                                                        setIsEditingAuction(false);
                                                        ddCacheRef.current = {}; setRawDdResults({});
                                                    }} style={{ ...btnBase, background: '#26A65B', color: '#fff' }}>Save</button>
                                                </div>
                                            </div>
                                        );
                                    })()} */}
                                </div>
                            ) :
                            /* State 2: End of Game / Claim */
                            isPlayPhase && ((step === maxStep && hasEndScreen && !isEditingPlay) || (isEditingPlay && playStep === 52)) ? (
                                
                                <div style={{
                                    background: 'rgba(13, 14, 21, 0.95)', 
                                    border: '1px solid rgba(255,215,0,0.8)', // Thinner border
                                    padding: '12px 24px', // Tighter padding
                                    borderRadius: 12, 
                                    textAlign: 'center', 
                                    boxShadow: '0 0 20px rgba(0,0,0,0.8), 0 0 10px rgba(255,215,0,0.1)', // Subtler backdrop
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 6 // Tighter gap
                                }}>
                                {/* <div style={{
                                    background: 'rgba(13, 14, 21, 0.95)', 
                                    border: '2px solid rgba(255, 217, 0, 0.43)', 
                                    padding: '20px 40px', 
                                    borderRadius: 16, 
                                    textAlign: 'center', 
                                    boxShadow: '0 0 40px rgba(0,0,0,0.8), 0 0 20px rgba(255,215,0,0.1)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10
                                }}> */}
                                    <div style={{color: 'rgba(255, 217, 0, 0.66)', fontSize: 22, fontWeight: '900', textTransform: 'uppercase', letterSpacing: 2}}>
                                        {isEditingPlay ? '(Result)' :(details.claim ? 'Claimed' : 'Result')}
                                    </div>
                                    
                                    {(() => {
                                        let displayRes = cData.res;
                                        let displayTricks = cData.tricks;
                                        let displayHomeScore = roomName === 'Away' ? -rawScore : rawScore;

                                        // Calculate the dynamic result if we are at the end of a hypothetical timeline
                                        if (isEditingPlay && playStep === 52 && !cData.isPass) {
                                            const declarerSide = (cData.declarer === 'N' || cData.declarer === 'S') ? 'NS' : 'EW';
                                            
                                            // Add the final trick sitting on the table to the permanent tally
                                            const finalTricksNS = tableState.tricksWon.NS + (tableState.currentTrick.length === 4 && (tableState.trickWinner === 'N' || tableState.trickWinner === 'S') ? 1 : 0);
                                            const finalTricksEW = tableState.tricksWon.EW + (tableState.currentTrick.length === 4 && (tableState.trickWinner === 'E' || tableState.trickWinner === 'W') ? 1 : 0);
                                            
                                            const decTricks = declarerSide === 'NS' ? finalTricksNS : finalTricksEW;
                                            const target = parseInt(cData.level) + 6;
                                            const diff = decTricks - target;

                                            if (diff === 0) { displayRes = '='; displayTricks = ''; }
                                            else if (diff > 0) { displayRes = '+'; displayTricks = diff.toString(); }
                                            else { displayRes = ''; displayTricks = diff.toString(); } // diff is already negative, so it prints "-1"

                                            displayHomeScore = null; // Hide the raw numerical score during hypotheticals to avoid confusion
                                        }

                                        return (
                                            <>
                                                <div style={{ fontSize: 18, fontWeight: 'bold', color: '#fff' }}>
                                                    {cData.level}
                                                    <span style={{color: (cData.suit === 'C' || cData.suit === 'S') ? '#fff' : SUIT_COLORS[cData.suit] || '#fff'}}>
                                                        {SUIT_SYMBOLS[cData.suit] || 'NT'}
                                                    </span>
                                                    {cData.dbl}{cData.declarer} <span style={{color: '#FFD700'}}>{displayRes}{displayTricks}</span>
                                                </div>
                                                
                                                {displayHomeScore !== null && (
                                                    <>
                                                        <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', width: '60%', margin: '5px auto' }} />
                                                        <div style={{color: '#A0AABF', fontSize: 16, fontWeight: 'bold'}}>
                                                            {roomName === 'Away' ? "EW: " : "NS: "} 
                                                            <span style={{color: displayHomeScore > 0 ? WIN_COLOR : (displayHomeScore < 0 ? LOSE_COLOR : '#fff')}}>
                                                                {displayHomeScore > 0 ? `+${displayHomeScore}` : displayHomeScore}
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            ) : 
                            /* State 3: Active Trick Area */
                            (
                                <div style={{ width: 140, height: 140, background: '#1A4A28', borderRadius: '16px', border: '2px solid rgba(255,255,255,0.1)', position: 'relative', boxShadow: 'inset 0 0 30px rgba(0,0,0,0.8)', overflow: 'hidden' }}>
                                    
                                   

                                    <div style={{ position: 'absolute', top: 4, left: 6, color: '#A0AABF', fontSize: 10, fontWeight: 'bold', zIndex: 6 }}>NS: <span style={{color: '#fff'}}>{tableState.tricksWon.NS}</span></div>
                                    <div style={{ position: 'absolute', bottom: 4, right: 6, color: '#A0AABF', fontSize: 10, fontWeight: 'bold', zIndex: 6 }}>EW: <span style={{color: '#fff'}}>{tableState.tricksWon.EW}</span></div>
                                    
                                    {tableState.currentTrick.map((play, i) => {
                                        const isWinner = tableState.currentTrick.length === 4 && play.seat === tableState.trickWinner;
                                        const isMostRecent = i === tableState.currentTrick.length - 1;
                                        const recentScore = (isMostRecent && ddActive) ? ddCacheRef.current[play.card.id] : null;
                                        
                                        const pos = {
                                            N: { top: 10, left: '50%', transform: 'translateX(-50%)', origin: 'translate(-50%, -60px)' },
                                            S: { bottom: 10, left: '50%', transform: 'translateX(-50%)', origin: 'translate(-50%, 60px)' },
                                            W: { left: 10, top: '50%', transform: 'translateY(-50%)', origin: 'translate(-60px, -50%)' },
                                            E: { right: 10, top: '50%', transform: 'translateY(-50%)', origin: 'translate(60px, -50%)' }
                                        }[play.seat];
                                        
                                        return (
                                            <div key={play.card.id} style={{ 
                                                position: 'absolute', ...pos, zIndex: isWinner ? 10 : i + 6, 
                                                boxShadow: isWinner ? '0 0 8px rgba(255, 215, 0, 0.4)' : 'none',
                                                animation: isForward ? `slideIn_${play.card.id} 0.15s cubic-bezier(0.2, 0.8, 0.2, 1) forwards` : 'none'
                                            }}>
                                                <PlayingCard card={play.card} ddScore={recentScore} /> 
                                                <style>{`
                                                    @keyframes slideIn_${play.card.id} {
                                                        0% { opacity: 0; transform: ${pos.origin}; scale: 0.8; }
                                                        100% { opacity: 1; transform: ${pos.transform}; scale: 1; }
                                                    }
                                                `}</style>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="ha-hand-east">
                            <PlayerHand 
                                    seat="E" pName={pNames.E} realName={aliases?.E} 
                                    isVul={vulData.EW} teamColor={colors.EW} cards={tableState.hands.E} 
                                    ddScores={ddActive ? mappedDdScores : []} trump={cData.trump} 
                                    onCardClick={handleCardClick}
                                    onBadgeClick={handleBadgeClick}
                                    isHiddenRegion={hiddenRegions.E}
                                    flippedCards={flippedCards}
                                    isActiveTurn={activeSeat === 'E'}
                                    isBiddingPhase={isBiddingPhase}
                                    leadSuit={(tableState.currentTrick.length > 0 && tableState.currentTrick.length < 4) ? tableState.currentTrick[0].card.suit : null}
                                />
                        </div>
                        <div className="ha-hand-south">
                            {/* TRICK TRACKER: Pinned Left */}
                            <div style={{ position: 'absolute', left: 0, bottom: 0, pointerEvents: 'auto' }}>
                                <TrickTracker trickHistory={tableState.trickHistory} />
                            </div>

                            {/* PLAYER HAND: Centered */}
                            <div style={{ pointerEvents: 'auto' }}>
                                <PlayerHand 
                                    seat="S" pName={pNames.S} realName={aliases?.S} 
                                    isVul={vulData.NS} teamColor={colors.NS} cards={tableState.hands.S} 
                                    ddScores={ddActive ? mappedDdScores : []} trump={cData.trump} 
                                    onCardClick={handleCardClick}
                                    onBadgeClick={handleBadgeClick}
                                    isHiddenRegion={hiddenRegions.S}
                                    flippedCards={flippedCards}
                                    isActiveTurn={activeSeat === 'S'}
                                    isBiddingPhase={isBiddingPhase}
                                    leadSuit={(tableState.currentTrick.length > 0 && tableState.currentTrick.length < 4) ? tableState.currentTrick[0].card.suit : null}
                                />
                            </div>
                        </div>

                        {/* COMPACT BBO-STYLE LIVE EDITOR (Pinned bottom-right inside container) */}
                        {/* COMPACT BBO-STYLE LIVE EDITOR (Pinned bottom-right inside container) */}
                        {isEditingAuction && !isAuctionOver && (() => {
                            // 1. Base the legal rules entirely on what we have scrubbed to!
                            const { lastBidValue, canDouble, canRedouble, isAuctionOver } = getAuctionState(currentBids);
                            const suitVals = { 'C':0, 'D':1, 'H':2, 'S':3, 'N':4 };

                            const addBid = (b) => { 
                                // 2. Truncate the future and append the new bid
                                const newArr = [...currentBids, b];
                                setCustomBiddingStr(newArr.join(', ')); setTempAuctionLvl(null); 
                                // 3. Auto-advance the timeline to show the new bid
                                setStep(newArr.length);
                                ddCacheRef.current = {}; setRawDdResults({}); setDdTrigger(t => t + 1);
                            };
                            
                            const getBtnStyle = (baseBg, color, isPressed, isDisabled) => ({
                                flex: 1, padding: '6px 0', borderRadius: 6, fontSize: 14, fontWeight: '900',
                                cursor: isDisabled ? 'default' : 'pointer', transition: 'all 0.1s',
                                border: '1px solid rgba(0,0,0,0.1)', background: baseBg, color: color,
                                boxShadow: isPressed || isDisabled ? 'none' : '0 3px 0 rgba(0,0,0,0.2), 0 4px 6px rgba(0,0,0,0.3)',
                                transform: isPressed || isDisabled ? 'translateY(3px)' : 'none',
                                opacity: isDisabled ? 0.4 : 1,
                                display: 'flex', justifyContent: 'center', alignItems: 'center'
                            });

                            return (
                                <div style={{ 
                                    position: 'absolute', bottom: 0, right: 0, width: 140, 
                                    background: '#121212', borderRadius: 4, border: '1px solid #2a2a2a', 
                                    padding: '8px', display: 'flex', flexDirection: 'column', gap: 6, 
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)', zIndex: 1005, userSelect: 'none' 
                                }}>
                                    {/* Minimal Close */}
                                    {/* <button onClick={() => setIsEditingAuction(false)} style={{ position: 'absolute', top: 2, right: 4, background: 'transparent', color: '#444', border: 'none', cursor: 'pointer', fontSize: 10 }}>✕</button> */}

                                    {/* Row 1: Levels (7 Slots) */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, height: 20 }}>
                                        {[1, 2, 3, 4, 5, 6, 7].map(l => {
                                            const isLvlDisabled = l * 5 + 4 <= lastBidValue;
                                            const isSel = tempAuctionLvl === l;
                                            return (
                                                <button key={l} 
                                                    onClick={() => setTempAuctionLvl(isSel ? null : l)} 
                                                    style={{
                                                        visibility: isLvlDisabled ? 'hidden' : 'visible',
                                                        background: isSel ? '#555' : '#333',
                                                        color: isSel ? '#fff' : '#fff',
                                                        border: `1px solid ${isSel ? '#444' : '#222'}`,
                                                        borderRadius: 2, fontSize: 10, cursor: 'pointer', padding: 0, fontWeight: isSel ? 'bold' : 'normal'
                                                    }}
                                                >{l}</button>
                                            );
                                        })}
                                    </div>

                                    {/* Row 2: Suits (5 Slots) */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, height: 22 }}>
                                        {['C', 'D', 'H', 'S', 'N'].map(s => {
                                            const isSuitDisabled = !tempAuctionLvl || (tempAuctionLvl * 5 + suitVals[s] <= lastBidValue);
                                            return (
                                                <button key={s} 
                                                    onClick={() => addBid(`${tempAuctionLvl}${s}`)} 
                                                    style={{
                                                        visibility: isSuitDisabled ? 'hidden' : 'visible',
                                                        background: '#222',
                                                        color: (s === 'D' || s === 'H') ? '#FF0000 ' : '#EEE', // Desaturated colors
                                                        border: '1px solid #222',
                                                        borderRadius: 2, fontSize: 11, cursor: 'pointer', padding: 0
                                                    }}
                                                >{SUIT_SYMBOLS[s] || 'NT'}</button>
                                            );
                                        })}
                                    </div>

                                    {/* Row 3: Special Actions (3 Slots) */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: 2, height: 18 }}>
                                        <button onClick={() => addBid('P')} style={{ background: WIN_COLOR, color: '#fff', border: '0.5px solid rgb(255, 255, 255, 0.5)', borderRadius: 2, fontSize: 8, cursor: 'pointer', textTransform: 'uppercase' }}>
                                            Pass
                                        </button>
                                        
                                        <button onClick={() => addBid('X')} style={{ 
                                            visibility: canDouble ? 'visible' : 'hidden',
                                            background: LOSE_COLOR, color: '#FFF', border: '0.5px solid rgba(255, 255, 255, 0.5)', borderRadius: 4, fontSize: 8, cursor: 'pointer' 
                                        }}>DBL</button>
                                        
                                        <button onClick={() => addBid('XX')} style={{ 
                                            visibility: canRedouble ? 'visible' : 'hidden',
                                            background: '#4D7886', color: '#FFF', border: '0.5px solid rgb(255, 255, 255, 0.5)', borderRadius: 4, fontSize: 8, cursor: 'pointer' 
                                        }}>RDBL</button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                ) : (
                    <HandDiagram hands={tableState.hands} ddScores={mappedDdScores} />
                )}
            </div>
            {/* SECURE 3-COLUMN BOTTOM CONTROL BAR */}
            <div style={{ marginTop: 20, paddingTop: 15, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                
                {/* LEFT COLUMN: Actions & Reverts (Flex-start so they grow left-to-right) */}
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                    {/* {(playStep === 0 && !isAuctionOver) && (
                        <button 
                            onClick={() => { 
                                if (isEditingAuction){
                                    setIsEditingAuction(false);
                                }else{
                                    if (customBiddingStr === null) setCustomBiddingStr(activeBidding.join(', ')); 
                                    setTempAuctionLvl(null); setIsEditingAuction(true); 
                                    // setStep(activeBidding.length);
                                    setIsEditingPlay(false);
                                    if (deviationStep === null) setDeviationStep(step);
                                }
                            }}
                            style={{background: '#1A1D26', color: '#FFD700', border: '1px solid rgba(255, 215, 0, 0.4)', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 10, fontWeight: 'bold'}}
                        >
                            Edit
                        </button>
                    )} */}
                    {(() => {
                        // 1. Check if the string actually differs from the original database array
                        const originalBids = (details?.bidding || []).join(', ');
                        const hasBiddingChanged = customBiddingStr !== null && customBiddingStr !== originalBids;
                        
                        // 2. Strip spaces to prevent false positives (e.g. "4S S" vs "4SS")
                        const hasContractChanged = customContractStr !== null && customContractStr.replace(/\s+/g, '') !== (contractStr || '').replace(/\s+/g, '');
                        
                        const hasActualChanges = isEditingPlay || hasBiddingChanged || hasContractChanged;
                        
                        if (!hasActualChanges) return null;

                        return (
                            <button 
                                title="Revert all changes"
                                onClick={() => { 
                                    const snapStep = deviationStep !== null ? deviationStep : 0;
                                    
                                    setCustomBiddingStr(null); 
                                    setCustomContractStr(null);
                                    setIsEditingAuction(false); 
                                    setCustomPlaySequence(details?.play || []); 
                                    setIsEditingPlay(false); 
                                    setStep(snapStep); 
                                    setDeviationStep(null);
                                    ddCacheRef.current = {}; 
                                    setRawDdResults({}); 
                                    setDdTrigger(t => t + 1);
                                }}
                                style={{background: 'transparent', color: '#A0AABF', border: '1px solid rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4, transition: '0.2s'}}
                            >
                                <span style={{fontSize: 12, lineHeight: 1}}>↺</span> Revert
                            </button>
                        );
                    })()}
                </div>
                
                {/* CENTER COLUMN: Rigid Navigation Buttons (Always dead center) */}
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                    <button onClick={() => setStep(step > totalBids ? totalBids : 0)} style={{background: 'rgba(255,255,255,0.05)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11}}>|&lt;</button>
                    <button onClick={() => setStep(Math.max(0, step - 1))} style={{background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 'bold'}}>&lt; Prev</button>
                    

                    <span style={{color: '#FFD700', fontWeight: 'bold', fontSize: 11, width: 80, textAlign: 'center', whiteSpace: 'nowrap'}}>
                        {(!isFullAuctionOver || step < totalBids) ? `Bid ${step}` : (playStep === 0 ? 'Lead' : `Trick ${Math.floor((playStep - 1) / 4) + 1}`)}
                    </span>
                    {/* <span style={{color: '#FFD700', fontWeight: 'bold', fontSize: 11, width: 80, textAlign: 'center'}}>
                        {(!isPlayPhase || playStep === 0) ? (showFullAuction ? 'Auction' : `Bid ${step + 1}`) : `Trick ${Math.floor((playStep - 1) / 4) + 1}`}
                    </span> */}
                    
                    <button onClick={() => setStep(Math.min(maxStep, step + 1))} style={{background: '#6C5DD3', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 'bold'}}>Next &gt;</button>
                    <button onClick={() => setStep(step < totalBids ? totalBids : maxStep)} style={{background: 'rgba(255,255,255,0.05)', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11}}>&gt;|</button>
                </div>

                {/* RIGHT COLUMN: DD Toggle (Flex-end so it anchors to the right) */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button 
                        onClick={() => setDdActive(!ddActive)} 
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            background: ddActive ? 'rgba(74, 222, 128, 0.15)' : 'rgba(0,0,0,0.3)', 
                            color: ddActive ? '#4ADE80' : '#A0AABF', 
                            border: `1px solid ${ddActive ? 'rgba(74, 222, 128, 0.4)' : 'rgba(255,255,255,0.1)'}`, 
                            padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 'bold', transition: 'all 0.2s'
                        }}
                    >
                        📊 {ddActive ? 'Hide DD' : 'Show DD'}
                    </button>
                </div>

            </div>
        </div>
    );
};


// --- 3. MAIN COMPONENT ---
const HandAnalyzer = ({ board, players = [], aliases = {}, colors = {}, isActive, t1Data, t2Data, t1Color, t2Color, swapRooms }) => {
    const { width, height, isMobile } = useWindowSize();

    const { globalStyle } = useData();
    // Dynamically calculate the perfect scale multiplier for the rooms
    

        
    // PERSISTENT SETTINGS
    const [roomLayout, setRoomLayout] = usePersistentState('ha_roomLayout', 'both'); 
    const [displayMode, setDisplayMode] = usePersistentState('ha_displayMode', 'visual'); 
    const [hidePlayed, setHidePlayed] = usePersistentState('ha_hidePlayed', true);
    const [showFullAuction, setShowFullAuction] = usePersistentState('ha_showFullAuction', false);
    const [hideAuctionInPlay, setHideAuctionInPlay] = usePersistentState('ha_hideAuctionInPlay', false);
    const [ddActive, setDdActive] = usePersistentState('bridge_dd_active', false);
    const [showDdTable, setShowDdTable] = useState(false);
    const [ddTableData, setDdTableData] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [parScore, setParScore] = useState(null);
    const workerRef = useRef(null);

    const [fullscreen, setFullScreen] = useState(false)

    const roomScale = useMemo(() => {
        if (!fullscreen || !width || !height) return 1;
        
        // Available width: split in half if showing both rooms, minus padding
        const availableWidth = roomLayout === 'both' ? (width / 2)-20 : width-40;
        // Available height: subtract the top bar and padding
        const availableHeight = height; 
        
        // 600x550 is the natural pixel bounding box of a RoomAnalyzer
        return Math.min(availableWidth / (600), availableHeight / (620));
    }, [fullscreen, width, height, roomLayout]);

    useEffect(() => {
        if (fullscreen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => { document.body.style.overflow = 'auto'; };
    }, [fullscreen]);

    useEffect(() => {
        if (isActive && !workerRef.current) {
            workerRef.current = new Worker(`/ddsWorker.js?v=${DDS_WASM_VERSION}`);
            workerRef.current.onmessage = (e) => {
                if (e.data.type === 'TABLE_RESULT') {
                    setDdTableData(e.data.data);
                }
                if (e.data.parScore || e.data.par) {
                    setParScore(e.data.parScore || e.data.par);
                }
            };
        }
        return () => {
            // Unconditionally terminate to prevent React Router crashing
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
        };
    }, [isActive]);

    const fetchTable = () => {
        setShowDdTable(!showDdTable);
        if (!ddTableData && workerRef.current) {
            // Reconstruct a clean PBN from the board details. 
            // (Use the Open room hands as the base)
            const baseHands = reconstructHands(board?.details?.o?.hands);
            const formatOpen = (h) => {
                const getS = (suit) => h.filter(c=>c.suit===suit).map(c=>c.rank==='10'?'T':c.rank).join('');
                return `${getS('S')}.${getS('H')}.${getS('D')}.${getS('C')}`;
            };
            const pbn = `N:${formatOpen(baseHands.N)} ${formatOpen(baseHands.E)} ${formatOpen(baseHands.S)} ${formatOpen(baseHands.W)}`;
            workerRef.current.postMessage({ action: 'CALC_DD_TABLE', pbn });
        }
    };

    const openAliases = t1Data ? { N: t1Data?.north, S: t1Data?.south, E: t2Data?.east, W: t2Data?.west } : aliases;
    const closedAliases = t2Data ? { N: t2Data?.north, S: t2Data?.south, E: t1Data?.east, W: t1Data?.west } : aliases;
    
    const openColors = t1Color ? { NS: t1Color, EW: t2Color } : colors;
    const closedColors = t2Color ? { NS: t2Color, EW: t1Color } : colors;

    const homeDetails = swapRooms ? board?.details?.c : board?.details?.o;
    const awayDetails = swapRooms ? board?.details?.o : board?.details?.c;
    const homeContract = swapRooms ? board?.room2 : board?.room1;
    const awayContract = swapRooms ? board?.room1 : board?.room2;
    const homeRaw = swapRooms ? board?.raw2 : board?.raw1;
    const awayRaw = swapRooms ? board?.raw1 : board?.raw2;
    const homeAliases = swapRooms ? closedAliases : openAliases;
    const awayAliases = swapRooms ? openAliases : closedAliases;
    const homeColors = swapRooms ? closedColors : openColors;
    const awayColors = swapRooms ? openColors : closedColors;

    const isSingleView = roomLayout === 'open' || roomLayout === 'closed';

    const analyzerContent = (
        <div style={
            fullscreen ? {
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                width: '100vw', height: '100vh',
                background: `linear-gradient(135deg, ${globalStyle?.bgStart || '#2D2B3F'} 0%, ${globalStyle?.bgEnd || '#0D0E15'} 100%)`,
                // background: 'rgb(51, 51, 51)', // Solid background to hide the app behind it
                zIndex: 999999, // Guaranteed to cover your nav bar!
                padding: '20px', boxSizing: 'border-box', overflowY: 'auto'
            } : { padding: '5px 0' }
        }>
            <style>{`

            .ha-table-container {
                display: grid;
                grid-template-columns: minmax(130px, 1fr) 160px minmax(130px, 1fr);
                /* CRITICAL: 1fr pushes hands to the edges to perfectly scale vertical space! */
                grid-template-rows: 1fr 160px 1fr; 
                gap: 8px;
                width: 100%; 
                height: 100%; /* Force it to fill the newly available vertical space */
                max-width: 800px; 
                margin: 0 auto;
                background: transparent; 
                padding: 0; 
            }
            .ha-hand-north { 
                grid-column: 1 / span 3; 
                grid-row: 1; 
                display: flex; 
                justify-content: center; 
                align-items: flex-start; 
                position: relative; 
                z-index: 1000;
                pointer-events: none; 
            }
            .ha-hand-south { 
                grid-column: 1 / span 3; 
                grid-row: 3; 
                display: flex; 
                justify-content: center; 
                align-items: flex-end; 
                position: relative; 
                z-index: 1000; 
                pointer-events: none; 
            }
            .ha-hand-west { grid-column: 1; grid-row: 2; display: flex; align-items: center; justify-content: flex-start; }
            .ha-hand-east { grid-column: 3; grid-row: 2; display: flex; align-items: center; justify-content: flex-end; }
            .ha-trick-area { grid-column: 2; grid-row: 2; display: flex; justify-content: center; align-items: center; position: relative; }
            
            .ha-hide-scroll::-webkit-scrollbar { display: none; }
            .ha-settings-menu {
                position: absolute; right: 0; top: 100%; margin-top: 5px;
                background: rgba(20, 24, 34, 0.95); border: 1px solid rgba(255,255,255,0.1);
                border-radius: 8px; padding: 10px; box-shadow: 0 10px 25px rgba(0,0,0,0.5);
                z-index: 1000; width: max-content; display: flex; flex-direction: column; gap: 8px;
            }
        `}</style>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 5, background: fullscreen ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0,0,0,0.2)', padding: '10px 15px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
    
                {/* 1. Side-by-Side Room Buttons */}
                <div style={{ display: 'flex', background: 'rgba(0,0,0,0.4)', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {['both', 'home', 'away'].map(mode => (
                        <button 
                            key={mode}
                            onClick={() => setRoomLayout(mode)}
                            style={{ 
                                background: roomLayout === mode ? '#6C5DD3' : 'transparent', 
                                color: roomLayout === mode ? '#fff' : '#A0AABF', 
                                border: 'none', padding: '6px 14px', fontSize: 11, fontWeight: 'bold', cursor: 'pointer', textTransform: 'capitalize',
                                transition: 'background 0.2s'
                            }}
                        >
                            {mode}
                        </button>
                    ))}
                </div>

                {/* 3. DD Table Toggle Button */}
                <button onClick={fetchTable} style={{ background: showDdTable ? 'rgba(255,255,255,0.1)' : 'transparent', color: showDdTable ? '#fff' : '#A0AABF', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s' }}>
                    DD Table
                </button>
                

                {!isMobile && (
                    <button 
                        onClick={() => setFullScreen(!fullscreen)} 
                        style={{ 
                            background: fullscreen ? 'rgba(108, 93, 211, 0.2)' : 'transparent', 
                            color: fullscreen ? '#fff' : '#A0AABF', 
                            border: `1px solid ${fullscreen ? '#6C5DD3' : 'rgba(255,255,255,0.2)'}`, 
                            borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', marginRight: 'auto'
                        }}
                    >
                        {fullscreen ? 'Exit Full Screen' : 'Full Screen'}
                    </button>
                )}
                
            </div>

            <div style={{ display: 'flex', gap: 15, width: '100%', flex: 1, flexDirection: (isMobile || isSingleView) ? 'column' : 'row', justifyContent: 'center', minHeight: 0}}>
                {(() => {
                    return (
                        <>
                            <div style={{ display: 'flex', gap: 10, width: '100%', flexDirection: (isMobile || isSingleView) ? 'column' : 'row', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
                                {/* FIX: maxWidth is now dynamically tethered to 100vh to enforce an aspect ratio! */}
                                <div style={{ display: (roomLayout === 'both' || roomLayout === 'home') ? 'flex' : 'none' }}>
                                    <RoomAnalyzer 
                                        details={homeDetails} roomName="Home" isActive={isActive} contractStr={homeContract} rawScore={homeRaw} 
                                        boardNum={board?.boardNum} players={players} aliases={homeAliases} colors={homeColors}
                                        displayMode={displayMode} hidePlayed={hidePlayed} showFullAuction={showFullAuction} hideAuctionInPlay={hideAuctionInPlay} ddActive={ddActive}
                                        ddTableData={ddTableData}
                                        showDdTable={showDdTable}
                                        scale={roomScale}
                                        isFullscreen={fullscreen}
                                    />
                                </div>
                                <div style={{ display: (roomLayout === 'both' || roomLayout === 'away') ? 'flex' : 'none' }}>
                                    <RoomAnalyzer 
                                        details={awayDetails} roomName="Away" isActive={isActive} contractStr={awayContract} rawScore={awayRaw}
                                        boardNum={board?.boardNum} players={players} aliases={awayAliases} colors={awayColors}
                                        displayMode={displayMode} hidePlayed={hidePlayed} showFullAuction={showFullAuction} hideAuctionInPlay={hideAuctionInPlay} ddActive={ddActive} ddTableData={ddTableData}
                                        showDdTable={showDdTable}
                                        scale={roomScale}
                                        isFullscreen={fullscreen}
                                    />
                                </div>
                            </div>
                        </>
                    );
                })()}
            </div>
        </div>
    );
    return fullscreen ? createPortal(analyzerContent, document.body) : analyzerContent;
};

export default HandAnalyzer;